import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Building2,
  Globe,
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Plus,
  Search,
  Settings,
  Shield,
  Edit2,
  Trash2,
  MoreVertical,
  Archive,
  ArchiveRestore,
  RotateCcw,
  Upload,
  FileText as FileIcon,
  X,
  Menu,
  Download,
  BarChart3,
  Grid3x3,
  List,
  AlignJustify,
  Users,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Eye
} from 'lucide-react'
import { fetchWithAuth } from '../lib/api'
import TPRMSettings from './TPRMSettings'
import VendorRegistry from './VendorRegistry'
import TPRMDashboard from './TPRMDashboard'
import SubprocessorManager from './SubprocessorManager'

interface DataAccessQuestionnaire {
  servicePurpose: string
  accessCustomerData: boolean | null
  accessEmployeePII: boolean | null
  accessInternalData: boolean | null
  dataTypes: string[]
  additionalContext: string
}

interface VendorSubmission {
  vendorName: string
  websiteUrl: string
  questionnaire: DataAccessQuestionnaire
  isSubprocessor?: boolean
  purpose?: string // 3-word purpose statement
  aiSummary?: string // Vendor description
  contractValue?: number // Annual contract value in dollars
  numberOfLicenses?: number // Number of licenses/seats purchased
}

interface VendorAssessment {
  id: string
  vendorName: string
  websiteUrl: string
  isSubprocessor?: boolean // Flag indicating if vendor is a subprocessor
  questionnaire?: DataAccessQuestionnaire // Optional for backward compatibility
  useCase?: string // Legacy field for old assessments
  dataClassification: 'L0' | 'L1' | 'L2' | 'L3'
  classificationReasoning: string // Reasoning for classification level
  status: 'auto-approved' | 'manager-approval' | 'exception-required' | 'exception-approved' | 'exception-rejected' | 'analyzing' | 'security-review'
  securityPosture: {
    hasPrivacyPolicy: boolean
    hasSoc2: boolean
    hasIso27001: boolean
    hasPenetrationTesting: boolean
    hasDpa: boolean
    trustCenterUrl?: string
  }
  aiSummary: string
  purpose?: string // 3-word purpose statement
  contractValue?: number // Annual contract value in dollars
  numberOfLicenses?: number // Number of licenses/seats purchased
  submittedAt: string
  submittedBy: string
  // Uncertain attestations that need user confirmation
  uncertainAttestations?: string[]
  webSearchSummary?: string
  // Uploaded questionnaire file metadata
  uploadedFile?: {
    filename: string
    uploadedAt: string
    url: string
  }
  // Attestation artifacts (reference URLs and uploaded files)
  attestationArtifacts?: {
    [attestationId: string]: {
      referenceUrl?: string
      uploadedFileName?: string
      uploadedFileKey?: string
    }
  }
  // Exception approval workflow
  exceptionRequest?: {
    reason: string
    requestedBy: string
    requestedAt: string
    approvalScreenshot?: {
      filename: string
      uploadedAt: string
      fileKey: string
    }
  }
  exceptionReview?: {
    reviewedBy: string
    reviewedAt: string
    decision: 'approved' | 'rejected'
    comments: string
  }
  // Archive and delete metadata
  archived?: boolean
  archivedAt?: string
  archivedBy?: string
  deleted?: boolean
  deletedAt?: string
  deletedBy?: string
}

interface VendorRiskAssessmentProps {
  initialTab?: 'assessments' | 'settings'
}

const VendorRiskAssessment: React.FC<VendorRiskAssessmentProps> = ({ initialTab }) => {
  // Check URL hash for initial tab (e.g., #tprm-assessments)
  const getInitialTab = (): 'assessments' | 'registry' | 'subprocessors' | 'settings' => {
    // If initialTab prop is provided, use it
    if (initialTab) {
      return initialTab
    }

    const hash = window.location.hash.replace('#', '')
    if (hash.startsWith('tprm-')) {
      const subTab = hash.substring(5) // Remove 'tprm-' prefix
      const validTabs = ['assessments', 'registry', 'subprocessors', 'settings']
      if (validTabs.includes(subTab)) {
        return subTab as any
      }
    }
    return 'assessments'
  }

  const [activeTab, setActiveTab] = useState<'assessments' | 'registry' | 'subprocessors' | 'settings'>(getInitialTab())
  const [subMenuOpen, setSubMenuOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [vendors, setVendors] = useState<VendorAssessment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Listen for hash changes to support tour navigation
  useEffect(() => {
    const handleHashChange = () => {
      const newTab = getInitialTab()
      if (newTab !== activeTab) {
        setActiveTab(newTab)
      }
    }
    window.addEventListener('hashchange', handleHashChange)
    return () => window.removeEventListener('hashchange', handleHashChange)
  }, [activeTab])
  const [formStep, setFormStep] = useState(0)
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0)
  const [subprocessors, setSubprocessors] = useState<any[]>([])
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [editingVendor, setEditingVendor] = useState<VendorAssessment | null>(null)
  const [viewFilter, setViewFilter] = useState<'active' | 'archived' | 'deleted'>('active')
  const [hasApiKey, setHasApiKey] = useState<boolean | null>(null)
  const [discovering, setDiscovering] = useState(false)
  const [suggestedClassification, setSuggestedClassification] = useState<{
    level: 'L0' | 'L1' | 'L2' | 'L3'
    reasoning: string
  } | null>(null)
  const [aiSuggestedAnswers, setAiSuggestedAnswers] = useState<{
    accessCustomerData?: { suggested: boolean; reasoning: string }
    accessEmployeePII?: { suggested: boolean; reasoning: string }
    accessInternalData?: { suggested: boolean; reasoning: string }
  } | null>(null)
  const [answerOverrides, setAnswerOverrides] = useState<{
    accessCustomerData?: string
    accessEmployeePII?: string
    accessInternalData?: string
  }>({})
  const [uncertainAttestations, setUncertainAttestations] = useState<Array<{ id: string; label: string; required: boolean }>>([])
  const [confirmedAttestations, setConfirmedAttestations] = useState<string[]>([])
  const [attestationFiles, setAttestationFiles] = useState<Map<string, File>>(new Map())
  const [attestationUrls, setAttestationUrls] = useState<Map<string, string>>(new Map())
  const [analyzingVendor, setAnalyzingVendor] = useState(false)
  const [analyzingPrivacyPolicy, setAnalyzingPrivacyPolicy] = useState(false)
  const [privacyPolicyAnalysisStatus, setPrivacyPolicyAnalysisStatus] = useState('Analyzing Privacy Policy...')
  const [privacyPolicyAnalysis, setPrivacyPolicyAnalysis] = useState<{
    privacyPolicyUrl?: string
    dpaUrl?: string
    accessCustomerData: boolean
    accessEmployeePII: boolean
    dataTypes: string[]
    analysisReason: string
  } | null>(null)
  // Cache for privacy policy analysis results keyed by "vendorName|websiteUrl"
  const [privacyPolicyCache, setPrivacyPolicyCache] = useState<Map<string, {
    privacyPolicyUrl?: string
    dpaUrl?: string
    accessCustomerData: boolean
    accessEmployeePII: boolean
    dataTypes: string[]
    analysisReason: string
  }>>(new Map())
  const [searchingDPA, setSearchingDPA] = useState(false)
  const [dpaUrl, setDpaUrl] = useState<string | null>(null)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [uploadingFile, setUploadingFile] = useState(false)
  const [selectedReviewYear, setSelectedReviewYear] = useState<string | null>(null)
  const [showReviewedSubprocessors, setShowReviewedSubprocessors] = useState(true)
  const [showExceptionModal, setShowExceptionModal] = useState(false)
  const [exceptionVendor, setExceptionVendor] = useState<VendorAssessment | null>(null)
  const [showExportMenu, setShowExportMenu] = useState(false)
  const exportMenuRef = React.useRef<HTMLDivElement>(null)
  const [exceptionReason, setExceptionReason] = useState('')
  const [exceptionScreenshot, setExceptionScreenshot] = useState<File | null>(null)
  const [submittingException, setSubmittingException] = useState(false)

  // Exception review states (for security team)
  const [reviewingVendorId, setReviewingVendorId] = useState<string | null>(null)
  const [reviewComments, setReviewComments] = useState('')
  const [submittingReview, setSubmittingReview] = useState(false)

  // Bulk-add subprocessor selection
  const [selectedSubprocessorIds, setSelectedSubprocessorIds] = useState<Set<string>>(new Set())

  // AbortController ref to prevent race conditions in vendor analysis
  const analyzeVendorAbortControllerRef = useRef<AbortController | null>(null)

  // Grid view and drag-and-drop
  const [viewMode, setViewMode] = useState<'list' | 'grid' | 'rows'>('rows')
  const [draggingOver, setDraggingOver] = useState<string | null>(null)
  const [uploadingForVendor, setUploadingForVendor] = useState<string | null>(null)
  const [bulkAddProgress, setBulkAddProgress] = useState<{ current: number; total: number } | null>(null)
  const [sortColumn, setSortColumn] = useState<'vendor' | 'status' | 'classification' | 'date'>('date')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [editMode, setEditMode] = useState(false)
  const [quickEditVendorId, setQuickEditVendorId] = useState<string | null>(null)
  const [editingClassification, setEditingClassification] = useState<{[key: string]: 'L0' | 'L1' | 'L2' | 'L3'}>({})
  const [uploadingDocs, setUploadingDocs] = useState<{[key: string]: File | null}>({})
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(new Set())

  // CSV upload states
  const [showCsvUpload, setShowCsvUpload] = useState(false)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [csvUploadResults, setCsvUploadResults] = useState<{success: number; errors: string[]}>({success: 0, errors: []})
  const [uploadingCsv, setUploadingCsv] = useState(false)
  const [csvProcessingProgress, setCsvProcessingProgress] = useState<{current: number; total: number}>({current: 0, total: 0})

  // AI Generation modal states
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [generateDescription, setGenerateDescription] = useState(true)
  const [generatePurpose, setGeneratePurpose] = useState(true)
  const [generatingContent, setGeneratingContent] = useState(false)
  const [generateResults, setGenerateResults] = useState<{success: number; errors: string[]}>({success: 0, errors: []})
  const [generateProgress, setGenerateProgress] = useState<{current: number; total: number}>({current: 0, total: 0})

  // Vendor detail view state
  const [viewingVendor, setViewingVendor] = useState<VendorAssessment | null>(null)



  const [formData, setFormData] = useState<VendorSubmission>({
    vendorName: '',
    websiteUrl: '',
    isSubprocessor: false,
    purpose: '',
    aiSummary: '',
    contractValue: undefined,
    numberOfLicenses: undefined,
    questionnaire: {
      servicePurpose: '',
      accessCustomerData: null,
      accessEmployeePII: null,
      accessInternalData: null,
      dataTypes: [],
      additionalContext: ''
    }
  })

  // Helper function to extract FQDN from URL (removes path, query, hash)
  const extractFQDN = (url: string): string => {
    try {
      if (!url) return url

      // If URL doesn't start with http/https, add https://
      const normalizedUrl = url.match(/^https?:\/\//) ? url : `https://${url}`
      const urlObj = new URL(normalizedUrl)

      // Return just the protocol and hostname (FQDN)
      return `${urlObj.protocol}//${urlObj.hostname}`
    } catch (error) {
      // If URL parsing fails, return original value
      return url
    }
  }

  // Load existing vendor assessments and subprocessors on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load vendor assessments from database (faster with indexes)
        const vendorsResponse = await fetchWithAuth('/api/vendor-risk-assessment-db')
        if (vendorsResponse.ok) {
          const vendorsData = await vendorsResponse.json()
          console.log('[TPRM] Loaded vendors from database:', vendorsData)
          console.log('[TPRM] Vendor statuses:', vendorsData.map((v: any) => ({ name: v.vendorName, status: v.status, deleted: v.deleted, archived: v.archived })))
          setVendors(vendorsData)
        } else {
          console.error('[TPRM] Failed to load vendors:', vendorsResponse.status, vendorsResponse.statusText)
        }

        // Load subprocessors
        const subprocessorsResponse = await fetchWithAuth('/api/subprocessors')
        if (subprocessorsResponse.ok) {
          const subprocessorsData = await subprocessorsResponse.json()
          const vendors = subprocessorsData.subprocessors || []
          setSubprocessors((vendors || []).filter((s: any) => s.isActive && !s.archived && !s.deleted))
        }
      } catch (error) {
        console.error('Error loading data:', error)
        const errorMsg = error instanceof Error ? error.message : 'Unknown error'
        setError(`Failed to load vendor data: ${errorMsg}. Please refresh the page.`)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [])

  // Check if Anthropic API key exists
  useEffect(() => {
    const checkApiKey = async () => {
      try {
        const response = await fetchWithAuth('/api/admin/tenant-secrets', {
          method: 'GET'
        })
        if (response.ok) {
          const data = await response.json()
          setHasApiKey(data.metadata?.claudeApiKey || false)
          console.log('[Vendor Assessment] Anthropic API key exists:', data.metadata?.claudeApiKey || false)
        } else {
          setHasApiKey(false)
        }
      } catch (error) {
        console.error('[Vendor Assessment] Error checking API key:', error)
        setHasApiKey(false)
      }
    }
    checkApiKey()
  }, [])

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false)
      }
    }

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportMenu])

  const handleDelete = async (vendorId: string, vendorName: string) => {
    if (!confirm(`Are you sure you want to delete the assessment for "${vendorName}"? This action cannot be undone.`)) {
      return
    }

    setDeletingId(vendorId)
    try {
      const response = await fetchWithAuth(`/api/vendor-risk-assessment?id=${vendorId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to delete assessment')
      }

      // Remove from local state
      setVendors(vendors.filter(v => v.id !== vendorId))
    } catch (error) {
      console.error('Error deleting vendor:', error)
      alert('Failed to delete vendor assessment. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const handleEdit = (vendor: VendorAssessment) => {
    setEditingVendor(vendor)

    // Pre-fill form with existing data
    // Handle both new questionnaire format and legacy useCase format
    if (vendor.questionnaire) {
      setFormData({
        vendorName: vendor.vendorName,
        websiteUrl: vendor.websiteUrl,
        questionnaire: vendor.questionnaire,
        purpose: vendor.purpose || '',
        aiSummary: vendor.aiSummary || '',
        contractValue: vendor.contractValue,
        numberOfLicenses: vendor.numberOfLicenses,
        isSubprocessor: vendor.isSubprocessor
      })
    } else {
      // Legacy format - convert useCase to questionnaire
      setFormData({
        vendorName: vendor.vendorName,
        websiteUrl: vendor.websiteUrl,
        questionnaire: {
          servicePurpose: vendor.useCase || '',
          accessCustomerData: null,
          accessEmployeePII: null,
          accessInternalData: null,
          dataTypes: [],
          additionalContext: 'Re-assessment of existing vendor'
        },
        purpose: vendor.purpose || '',
        aiSummary: vendor.aiSummary || '',
        contractValue: vendor.contractValue,
        numberOfLicenses: vendor.numberOfLicenses,
        isSubprocessor: vendor.isSubprocessor
      })
    }

    // Load attestation artifacts if they exist
    if (vendor.attestationArtifacts) {
      console.log('[Vendor Edit] Loading attestation artifacts:', vendor.attestationArtifacts)

      // Create a reverse mapping from attestation IDs to labels
      // Based on the security posture field naming convention
      const attestationIdToLabel: { [key: string]: string } = {
        'hasPrivacyPolicy': 'Privacy Policy',
        'hasSoc2': 'SOC 2',
        'hasIso27001': 'ISO 27001',
        'hasPenetrationTesting': 'Penetration Testing',
        'hasDpa': 'DPA'
      }

      // Populate attestationUrls Map with existing reference URLs
      const newAttestationUrls = new Map<string, string>()
      const confirmedLabels: string[] = []

      for (const [attestationId, artifact] of Object.entries(vendor.attestationArtifacts)) {
        const label = attestationIdToLabel[attestationId]
        if (label) {
          // If this attestation has either a URL or an uploaded file, mark it as confirmed
          if (artifact.referenceUrl || artifact.uploadedFileName) {
            confirmedLabels.push(label)
          }

          // Load reference URL if it exists
          if (artifact.referenceUrl) {
            newAttestationUrls.set(label, artifact.referenceUrl)
            console.log(`[Vendor Edit] Loaded reference URL for ${label}:`, artifact.referenceUrl)
          }

          // Log uploaded file metadata if it exists
          if (artifact.uploadedFileName) {
            console.log(`[Vendor Edit] Found uploaded file for ${label}:`, artifact.uploadedFileName)
          }
        }
      }

      setAttestationUrls(newAttestationUrls)
      setConfirmedAttestations(confirmedLabels)
      console.log('[Vendor Edit] Confirmed attestations:', confirmedLabels)

      // Note: We cannot pre-populate attestationFiles Map because we only have metadata, not File objects
      // But we'll display information about uploaded files when the attestation step is shown
    }

    setShowForm(true)
    setFormStep(1)
  }

  const handleArchive = async (vendorId: string, vendorName: string) => {
    if (!confirm(`Archive the assessment for "${vendorName}"?`)) {
      return
    }

    try {
      const response = await fetchWithAuth(`/api/vendor-risk-assessment?id=${vendorId}&action=archive`, {
        method: 'PATCH'
      })

      if (!response.ok) {
        throw new Error('Failed to archive assessment')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))
    } catch (error) {
      console.error('Error archiving vendor:', error)
      alert('Failed to archive vendor assessment. Please try again.')
    }
  }

  const handleUnarchive = async (vendorId: string, vendorName: string) => {
    try {
      const response = await fetchWithAuth(`/api/vendor-risk-assessment?id=${vendorId}&action=unarchive`, {
        method: 'PATCH'
      })

      if (!response.ok) {
        throw new Error('Failed to unarchive assessment')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))
    } catch (error) {
      console.error('Error unarchiving vendor:', error)
      alert('Failed to unarchive vendor assessment. Please try again.')
    }
  }

  const handleMoveToRecycleBin = async (vendorId: string, vendorName: string) => {
    if (!confirm(`Move "${vendorName}" to recycle bin?`)) {
      return
    }

    try {
      const response = await fetchWithAuth(`/api/vendor-risk-assessment?id=${vendorId}&action=delete`, {
        method: 'PATCH'
      })

      if (!response.ok) {
        throw new Error('Failed to move to recycle bin')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))
    } catch (error) {
      console.error('Error moving to recycle bin:', error)
      alert('Failed to move vendor assessment to recycle bin. Please try again.')
    }
  }

  const handleRestore = async (vendorId: string, vendorName: string) => {
    try {
      const response = await fetchWithAuth(`/api/vendor-risk-assessment?id=${vendorId}&action=restore`, {
        method: 'PATCH'
      })

      if (!response.ok) {
        throw new Error('Failed to restore assessment')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))
    } catch (error) {
      console.error('Error restoring vendor:', error)
      alert('Failed to restore vendor assessment. Please try again.')
    }
  }

  const handlePermanentDelete = async (vendorId: string, vendorName: string) => {
    if (!confirm(`PERMANENTLY DELETE the assessment for "${vendorName}"? This action cannot be undone.`)) {
      return
    }

    setDeletingId(vendorId)
    try {
      const response = await fetchWithAuth(`/api/vendor-risk-assessment?id=${vendorId}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to permanently delete assessment')
      }

      // Remove from local state
      setVendors(vendors.filter(v => v.id !== vendorId))
    } catch (error) {
      console.error('Error permanently deleting vendor:', error)
      alert('Failed to permanently delete vendor assessment. Please try again.')
    } finally {
      setDeletingId(null)
    }
  }

  const toggleSubprocessorSelection = (subId: string) => {
    setSelectedSubprocessorIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(subId)) {
        newSet.delete(subId)
      } else {
        newSet.add(subId)
      }
      return newSet
    })
  }

  const toggleSelectAllSubprocessors = () => {
    if (selectedSubprocessorIds.size === pendingSubprocessors.length) {
      // Deselect all
      setSelectedSubprocessorIds(new Set())
    } else {
      // Select all
      setSelectedSubprocessorIds(new Set(pendingSubprocessors.map((sub: any) => sub.id)))
    }
  }

  const handleBulkAddPendingSubprocessors = async () => {
    if (selectedSubprocessorIds.size === 0) {
      alert('Please select at least one subprocessor to add.')
      return
    }

    if (!confirm(`Add ${selectedSubprocessorIds.size} selected subprocessor${selectedSubprocessorIds.size > 1 ? 's' : ''} to the assessment queue?\n\nThey will be added with "Pending Review" status and you can complete their assessments individually.`)) {
      return
    }

    setIsSubmitting(true)
    const successfulAdds: any[] = []
    const failedAdds: any[] = []

    // Filter to only selected subprocessors
    const selectedSubs = pendingSubprocessors.filter((sub: any) => selectedSubprocessorIds.has(sub.id))

    console.log('[Bulk Add] Starting bulk add for subprocessors:', selectedSubs.map(s => s.name))

    setBulkAddProgress({ current: 0, total: selectedSubs.length })

    try {
      for (let i = 0; i < selectedSubs.length; i++) {
        const sub = selectedSubs[i]
        try {
          console.log(`[Bulk Add] Adding subprocessor: ${sub.name}`)
          setBulkAddProgress({ current: i + 1, total: selectedSubs.length })

          // Create placeholder assessment record
          const response = await fetchWithAuth('/api/vendor-risk-assessment-bulk-add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              vendorName: sub.name,
              websiteUrl: sub.website || extractFQDN(sub.dpaUrl || sub.privacyUrl || ''),
              isSubprocessor: true,
              questionnaire: {
                servicePurpose: sub.purpose || 'Subprocessor - pending assessment',
                accessCustomerData: null,
                accessEmployeePII: null,
                accessInternalData: null,
                dataTypes: sub.dataAccess || [],
                additionalContext: 'Bulk-added from subprocessor list - requires full assessment'
              }
            })
          })

          if (response.ok) {
            const result = await response.json()
            console.log(`[Bulk Add] Successfully added ${sub.name}:`, result.assessment)
            successfulAdds.push(result.assessment)
          } else {
            const error = await response.json()
            console.error(`[Bulk Add] Failed to add ${sub.name}:`, error)
            if (error.assessment) {
              console.log(`[Bulk Add] Existing assessment details:`, {
                name: error.assessment.vendorName,
                id: error.assessment.id,
                status: error.assessment.status,
                archived: error.assessment.archived,
                deleted: error.assessment.deleted
              })
            }
            failedAdds.push(sub)
          }
        } catch (error) {
          console.error(`[Bulk Add] Error adding subprocessor ${sub.name}:`, error)
          failedAdds.push(sub)
        }
      }

      console.log(`[Bulk Add] Completed. Success: ${successfulAdds.length}, Failed: ${failedAdds.length}`)

      // Update local vendors state with successful adds
      if (successfulAdds.length > 0) {
        console.log('[Bulk Add] Updating local state with:', successfulAdds)
        setVendors([...vendors, ...successfulAdds])

        // Refresh data to sync with backend (from database for faster query)
        console.log('[Bulk Add] Refreshing vendor list from database...')
        const vendorsResponse = await fetchWithAuth('/api/vendor-risk-assessment-db')
        if (vendorsResponse.ok) {
          const vendorsData = await vendorsResponse.json()
          console.log('[Bulk Add] Received vendor data from database:', vendorsData)
          // Keep all vendors in state; filteredVendors useMemo handles display filtering
          setVendors(vendorsData)
        } else {
          console.error('[Bulk Add] Failed to refresh vendor list')
        }
      }

      if (failedAdds.length > 0) {
        alert(`Successfully added ${successfulAdds.length} subprocessors to the assessment queue.\n${failedAdds.length} failed to add.\n\nRefresh the page to see them appear in the vendor list.`)
      } else {
        alert(`Successfully added all ${successfulAdds.length} subprocessors to the assessment queue!\n\nThey will appear in the vendor list with "Pending Review" status momentarily. If they don't appear immediately, refresh the page.`)
      }

      // Clear selection after successful bulk-add
      setSelectedSubprocessorIds(new Set())
    } catch (error) {
      console.error('[Bulk Add] Error bulk-adding subprocessors:', error)
      alert('Failed to bulk-add subprocessors. Please try again.')
    } finally {
      setIsSubmitting(false)
      setBulkAddProgress(null)
    }
  }

  // Sorting handler for rows view
  const handleSort = (column: 'vendor' | 'status' | 'classification' | 'date') => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent, vendorId: string) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOver(vendorId)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOver(null)
  }

  const handleDrop = async (e: React.DragEvent, vendor: VendorAssessment) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggingOver(null)

    const files = Array.from(e.dataTransfer.files)
    if (files.length === 0) return

    // For exception-required vendors, treat drop as exception approval
    if (vendor.status === 'exception-required') {
      if (files.length > 1) {
        alert('Please drop only one file for exception approval.')
        return
      }
      await handleExceptionFileDrop(vendor, files[0])
      return
    }

    // For vendors with uncertain attestations, treat drop as attestation upload
    if (vendor.uncertainAttestations && vendor.uncertainAttestations.length > 0) {
      await handleAttestationFileDrop(vendor, files)
      return
    }

    alert('This vendor does not currently need file uploads. It may already be approved or require a different action.')
  }

  const handleExceptionFileDrop = async (vendor: VendorAssessment, file: File) => {
    const reason = prompt(`Provide a reason for the exception request for ${vendor.vendorName}:`)
    if (!reason) return

    setUploadingForVendor(vendor.id)
    try {
      const formData = new FormData()
      formData.append('assessmentId', vendor.id)
      formData.append('reason', reason)
      formData.append('screenshot', file)

      const response = await fetchWithAuth('/api/vendor-exception-request', {
        method: 'POST',
        body: formData
      })

      if (response.ok) {
        const result = await response.json()
        // Update local state
        setVendors(vendors.map(v => v.id === vendor.id ? result.assessment : v))
        alert('Exception request submitted successfully!')
      } else {
        const error = await response.json()
        alert(`Failed to submit exception: ${error.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error submitting exception:', error)
      alert('Failed to submit exception request. Please try again.')
    } finally {
      setUploadingForVendor(null)
    }
  }

  const handleAttestationFileDrop = async (vendor: VendorAssessment, files: File[]) => {
    if (!vendor.uncertainAttestations || vendor.uncertainAttestations.length === 0) {
      alert('No attestations needed for this vendor.')
      return
    }

    // If single file and single uncertain attestation, auto-assign
    if (files.length === 1 && vendor.uncertainAttestations.length === 1) {
      const attestation = vendor.uncertainAttestations[0]
      await uploadAttestationAndReanalyze(vendor, attestation.id, attestation.label, files[0])
      return
    }

    // Multiple files or multiple attestations - let user choose
    const attestationLabels = vendor.uncertainAttestations.map(a => a.label).join(', ')
    const selectedLabel = prompt(
      `Which attestation is this for?\n\nNeeded: ${attestationLabels}\n\nEnter the attestation name:`
    )

    if (!selectedLabel) return

    const attestation = vendor.uncertainAttestations.find(
      a => a.label.toLowerCase() === selectedLabel.toLowerCase()
    )

    if (!attestation) {
      alert(`Attestation "${selectedLabel}" not found. Please use one of: ${attestationLabels}`)
      return
    }

    await uploadAttestationAndReanalyze(vendor, attestation.id, attestation.label, files[0])
  }

  const handleAnalyzePrivacyPolicy = async (forceRefresh: boolean = false) => {
    if (!formData.vendorName || !formData.websiteUrl) {
      alert('Please provide vendor name and website URL first')
      return
    }

    // Generate cache key from vendor name and website URL
    const cacheKey = `${formData.vendorName.toLowerCase().trim()}|${formData.websiteUrl.toLowerCase().trim()}`

    // Check cache first (unless force refresh)
    if (!forceRefresh && privacyPolicyCache.has(cacheKey)) {
      console.log('[Privacy Policy Analysis] Using cached results for:', formData.vendorName)
      const cachedResult = privacyPolicyCache.get(cacheKey)!
      setPrivacyPolicyAnalysis(cachedResult)

      // Auto-populate checkboxes from cached data
      const updatedFormData = {
        ...formData,
        questionnaire: {
          ...formData.questionnaire,
          accessCustomerData: cachedResult.accessCustomerData,
          accessEmployeePII: cachedResult.accessEmployeePII
        },
        isSubprocessor: cachedResult.accessCustomerData ? true : formData.isSubprocessor
      }
      setFormData(updatedFormData)

      console.log('[Privacy Policy Analysis] Cache hit - skipping API call')
      return
    }

    setAnalyzingPrivacyPolicy(true)
    setPrivacyPolicyAnalysisStatus('Searching for Privacy Policy...')
    try {
      console.log('[Privacy Policy Analysis] Starting analysis for:', formData.vendorName, formData.websiteUrl)

      // Update status after 2 seconds to show DPA search
      setTimeout(() => {
        setPrivacyPolicyAnalysisStatus('Searching for DPA (Data Processing Agreement)...')
      }, 2000)

      // Update status after 5 seconds to show AI analysis
      setTimeout(() => {
        setPrivacyPolicyAnalysisStatus('Analyzing data access patterns with AI...')
      }, 5000)

      // Add 60 second timeout to prevent hanging
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 60000)

      const response = await fetchWithAuth('/api/vendor-risk-assessment-analyze-privacy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          vendorName: formData.vendorName,
          websiteUrl: formData.websiteUrl
        }),
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      console.log('[Privacy Policy Analysis] Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('[Privacy Policy Analysis] API error:', response.status, errorText)
        alert(`Privacy policy analysis failed: ${response.status} - ${errorText}`)
        return
      }

      const result = await response.json()
      console.log('[Privacy Policy Analysis] Result:', result)

      if (result.success) {
        // Store in both current state and cache
        setPrivacyPolicyAnalysis(result)
        setPrivacyPolicyCache(prevCache => {
          const newCache = new Map(prevCache)
          newCache.set(cacheKey, result)
          return newCache
        })
        console.log('[Privacy Policy Analysis] Analysis successful and cached for:', cacheKey)
        console.log('[Privacy Policy Analysis] Analysis details:', {
          privacyPolicyUrl: result.privacyPolicyUrl,
          dpaUrl: result.dpaUrl,
          accessCustomerData: result.accessCustomerData,
          accessEmployeePII: result.accessEmployeePII
        })
        // Auto-populate checkboxes and mark as subprocessor if customer data access detected
        const updatedFormData = {
          ...formData,
          questionnaire: {
            ...formData.questionnaire,
            accessCustomerData: result.accessCustomerData,
            accessEmployeePII: result.accessEmployeePII
          },
          // If vendor accesses customer data, mark as subprocessor (will require DPA)
          isSubprocessor: result.accessCustomerData ? true : formData.isSubprocessor
        }
        setFormData(updatedFormData)

        // Trigger re-analysis to update required attestations based on new classification
        if (result.accessCustomerData === true || result.accessEmployeePII === true) {
          console.log('[Privacy Policy Analysis] Triggering attestation re-analysis due to PII access detected')
          // Call analyze endpoint to refresh attestations with correct REQUIRED/OPTIONAL flags
          try {
            const analysisResponse = await fetchWithAuth('/api/vendor-risk-assessment-analyze', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...updatedFormData,
                analyzeOnly: true
              })
            })

            if (analysisResponse.ok) {
              const analysisResult = await analysisResponse.json()
              if (analysisResult.attestationsToConfirm && analysisResult.attestationsToConfirm.length > 0) {
                console.log('[Privacy Policy Analysis] Updated attestations based on new classification:', {
                  classification: analysisResult.dataClassification,
                  attestations: analysisResult.attestationsToConfirm
                })
                setUncertainAttestations(analysisResult.attestationsToConfirm)
              }
            }
          } catch (error) {
            console.error('[Privacy Policy Analysis] Failed to refresh attestations:', error)
            // Continue anyway - user can still complete the flow
          }
        }
      } else {
        console.error('[Privacy Policy Analysis] Result unsuccessful:', result.error)
        alert(`Privacy policy analysis failed: ${result.error}`)
      }
    } catch (error) {
      console.error('[Privacy Policy Analysis] Exception:', error)
      alert(`Failed to analyze privacy policy: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      console.log('[Privacy Policy Analysis] Complete, clearing loading state')
      setAnalyzingPrivacyPolicy(false)
    }
  }

  // Load cached privacy policy results when entering Step 2
  // Auto-trigger analysis ONCE if no cache exists (first time)
  React.useEffect(() => {
    if (formStep === 2 && formData.vendorName && formData.websiteUrl && !privacyPolicyAnalysis) {
      const cacheKey = `${formData.vendorName.toLowerCase().trim()}|${formData.websiteUrl.toLowerCase().trim()}`

      // Check if we have cached results
      if (privacyPolicyCache.has(cacheKey)) {
        // We have cached results - populate display state from cache WITHOUT triggering analysis
        console.log('[Privacy Policy Analysis] Loading cached results for:', formData.vendorName)
        const cachedResult = privacyPolicyCache.get(cacheKey)!
        setPrivacyPolicyAnalysis(cachedResult)

        // Auto-populate form data from cache
        if (cachedResult.accessCustomerData !== undefined) {
          setFormData(prev => ({
            ...prev,
            questionnaire: {
              ...prev.questionnaire,
              accessCustomerData: cachedResult.accessCustomerData
            }
          }))
        }
        if (cachedResult.accessEmployeePII !== undefined) {
          setFormData(prev => ({
            ...prev,
            questionnaire: {
              ...prev.questionnaire,
              accessEmployeePII: cachedResult.accessEmployeePII
            }
          }))
        }
      } else {
        // No cache - auto-trigger analysis for first time
        console.log('[Privacy Policy Analysis] No cache found, auto-triggering analysis for:', formData.vendorName)
        handleAnalyzePrivacyPolicy(false)
      }
    }
  }, [formStep, formData.vendorName, formData.websiteUrl, privacyPolicyAnalysis, privacyPolicyCache])

  // DPA URL search function (calls backend API to bypass CSP)
  const handleSearchDPA = async () => {
    if (!formData.websiteUrl) {
      console.log('[DPA Search] No website URL provided')
      return
    }

    setSearchingDPA(true)
    try {
      // Build list of URLs to try
      const urlsToTry: string[] = [formData.websiteUrl]

      // If we have a privacy policy URL, also try its base domain
      if (privacyPolicyAnalysis?.privacyPolicyUrl) {
        try {
          const privacyPolicyUrl = new URL(privacyPolicyAnalysis.privacyPolicyUrl)
          const privacyPolicyBase = `${privacyPolicyUrl.protocol}//${privacyPolicyUrl.host}`
          // Only add if it's different from the website URL
          if (privacyPolicyBase !== formData.websiteUrl && !urlsToTry.includes(privacyPolicyBase)) {
            urlsToTry.push(privacyPolicyBase)
            console.log('[DPA Search] Will also try privacy policy domain:', privacyPolicyBase)
          }
        } catch (e) {
          // Invalid URL, skip
        }
      }

      // Try each URL until we find a DPA
      for (const websiteUrl of urlsToTry) {
        console.log(`[DPA Search] Calling backend API to search for DPA at ${websiteUrl}`)

        const response = await fetchWithAuth('/api/vendor-risk-assessment-search-dpa', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ websiteUrl })
        })

        if (response.ok) {
          const result = await response.json()
          if (result.success && result.dpaUrl) {
            console.log(`[DPA Search] Found DPA at: ${result.dpaUrl}`)
            setDpaUrl(result.dpaUrl)
            return // Stop searching once we find a DPA
          } else {
            console.log(`[DPA Search] Could not find DPA at ${websiteUrl}`)
          }
        } else {
          console.log(`[DPA Search] API returned error for ${websiteUrl}: ${response.status}`)
        }
      }

      console.log('[DPA Search] Could not find DPA at any URL')
    } catch (error) {
      console.error('[DPA Search] Error:', error)
    } finally {
      setSearchingDPA(false)
    }
  }

  // Auto-populate Privacy Policy and DPA URLs when entering Step 5 (attestation step)
  React.useEffect(() => {
    if (formStep === 5 && uncertainAttestations.length > 0) {
      console.log('[Auto-populate] Entering attestation step')

      // Auto-populate Privacy Policy URL from analysis
      if (privacyPolicyAnalysis?.privacyPolicyUrl) {
        setAttestationUrls(prevUrls => {
          const newUrls = new Map(prevUrls)
          if (!newUrls.has('Privacy Policy')) {
            newUrls.set('Privacy Policy', privacyPolicyAnalysis.privacyPolicyUrl!)
            console.log('[Auto-populate] Set Privacy Policy URL:', privacyPolicyAnalysis.privacyPolicyUrl)
          }
          return newUrls
        })
        // Also check the checkbox when URL is auto-populated
        setConfirmedAttestations(prev => {
          if (!prev.includes('Privacy Policy')) {
            console.log('[Auto-populate] Checking Privacy Policy checkbox')
            return [...prev, 'Privacy Policy']
          }
          return prev
        })
      }

      // Auto-populate DPA URL from analysis if found
      if (privacyPolicyAnalysis?.dpaUrl) {
        console.log('[Auto-populate] DPA URL found in privacy policy analysis:', privacyPolicyAnalysis.dpaUrl)
        setDpaUrl(privacyPolicyAnalysis.dpaUrl)
        setAttestationUrls(prevUrls => {
          const newUrls = new Map(prevUrls)
          if (!newUrls.has('DPA')) {
            newUrls.set('DPA', privacyPolicyAnalysis.dpaUrl!)
            console.log('[Auto-populate] Set DPA URL:', privacyPolicyAnalysis.dpaUrl)
          }
          return newUrls
        })
        // Also check the checkbox when URL is auto-populated
        setConfirmedAttestations(prev => {
          if (!prev.includes('DPA')) {
            console.log('[Auto-populate] Checking DPA checkbox')
            return [...prev, 'DPA']
          }
          return prev
        })
      }

      // Fallback: Only search for DPA if not returned from privacy policy analysis
      if (!privacyPolicyAnalysis?.dpaUrl && !dpaUrl && !searchingDPA && formData.websiteUrl) {
        console.log('[Auto-populate] DPA not found in analysis, triggering fallback DPA search')
        handleSearchDPA()
      } else if (dpaUrl) {
        setAttestationUrls(prevUrls => {
          const newUrls = new Map(prevUrls)
          if (!newUrls.has('DPA')) {
            newUrls.set('DPA', dpaUrl)
            console.log('[Auto-populate] Set DPA URL:', dpaUrl)
          }
          return newUrls
        })
        // Also check the checkbox when URL is auto-populated
        setConfirmedAttestations(prev => {
          if (!prev.includes('DPA')) {
            console.log('[Auto-populate] Checking DPA checkbox')
            return [...prev, 'DPA']
          }
          return prev
        })
      }
    }
  }, [formStep, uncertainAttestations, privacyPolicyAnalysis, dpaUrl])

  // Update attestation URLs when DPA URL is found
  React.useEffect(() => {
    if (dpaUrl && formStep === 5) {
      setAttestationUrls(prevUrls => {
        const newUrls = new Map(prevUrls)
        if (!newUrls.has('DPA')) {
          newUrls.set('DPA', dpaUrl)
          console.log('[Auto-populate] DPA URL found and set:', dpaUrl)
        }
        return newUrls
      })
      // Also check the checkbox when URL is found
      setConfirmedAttestations(prev => {
        if (!prev.includes('DPA')) {
          console.log('[Auto-populate] Checking DPA checkbox after URL found')
          return [...prev, 'DPA']
        }
        return prev
      })
    }
  }, [dpaUrl, formStep])

  const uploadAttestationAndReanalyze = async (
    vendor: VendorAssessment,
    attestationId: string,
    attestationLabel: string,
    file: File
  ) => {
    setUploadingForVendor(vendor.id)
    try {
      // Upload the attestation file
      const uploadFormData = new FormData()
      uploadFormData.append('file', file)
      uploadFormData.append('vendorName', vendor.vendorName)
      uploadFormData.append('attestationId', attestationId)

      const uploadResponse = await fetchWithAuth('/api/vendor-risk-assessment-upload', {
        method: 'POST',
        body: uploadFormData
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload attestation file')
      }

      const uploadResult = await uploadResponse.json()

      // Re-analyze vendor with the new attestation
      const reanalyzePayload = {
        ...vendor,
        editingFromId: vendor.id,
        confirmedAttestations: [attestationId],
        attestationArtifacts: {
          [attestationId]: {
            uploadedFileName: uploadResult.file.filename,
            uploadedFileKey: uploadResult.file.key
          }
        }
      }

      const analyzeResponse = await fetchWithAuth('/api/vendor-risk-assessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reanalyzePayload)
      })

      if (analyzeResponse.ok) {
        const result = await analyzeResponse.json()
        // Update local state
        setVendors(vendors.map(v => v.id === vendor.id ? result.assessment : v))
        // Refresh data to get latest
        const vendorsResponse = await fetchWithAuth('/api/vendor-risk-assessment')
        if (vendorsResponse.ok) {
          const vendorsData = await vendorsResponse.json()
          // Keep all vendors in state; filteredVendors useMemo handles display filtering
          setVendors(vendorsData)
        }
        alert(`${attestationLabel} uploaded successfully! Vendor re-analyzed.`)
      } else {
        throw new Error('Failed to re-analyze vendor')
      }
    } catch (error) {
      console.error('Error uploading attestation:', error)
      alert('Failed to upload attestation and re-analyze. Please try again.')
    } finally {
      setUploadingForVendor(null)
    }
  }

  const toggleAttestationSelection = (attestationLabel: string) => {
    setConfirmedAttestations(prev => {
      const isSelected = prev.includes(attestationLabel)
      console.log('[Attestation Toggle]', {
        attestation: attestationLabel,
        isSelected,
        previousSelections: prev,
        action: isSelected ? 'removing' : 'adding'
      })
      if (isSelected) {
        const newState = prev.filter(a => a !== attestationLabel)
        console.log('[Attestation Toggle] New state:', newState)
        // Remove associated file if any
        setAttestationFiles(prevFiles => {
          const newFiles = new Map(prevFiles)
          newFiles.delete(attestationLabel)
          return newFiles
        })
        // Uncheck subprocessor if DPA is removed
        if (attestationLabel === 'DPA') {
          setFormData(prevData => ({ ...prevData, isSubprocessor: false }))
        }
        return newState
      } else {
        const newState = [...prev, attestationLabel]
        console.log('[Attestation Toggle] New state:', newState)
        // Auto-check subprocessor if DPA is selected
        if (attestationLabel === 'DPA') {
          setFormData(prevData => ({ ...prevData, isSubprocessor: true }))
        }
        return newState
      }
    })
  }

  const handleAttestationFileUpload = (attestationLabel: string, file: File | null) => {
    setAttestationFiles(prevFiles => {
      const newFiles = new Map(prevFiles)
      if (file) {
        newFiles.set(attestationLabel, file)
        // Clear URL if file is being uploaded
        setAttestationUrls(prevUrls => {
          const newUrls = new Map(prevUrls)
          newUrls.delete(attestationLabel)
          return newUrls
        })
        // Auto-check subprocessor if DPA file is uploaded
        if (attestationLabel === 'DPA') {
          setFormData(prevData => ({ ...prevData, isSubprocessor: true }))
        }
      } else {
        newFiles.delete(attestationLabel)
        // Uncheck subprocessor if DPA file is removed
        if (attestationLabel === 'DPA') {
          setFormData(prevData => ({ ...prevData, isSubprocessor: false }))
        }
      }
      return newFiles
    })
  }

  const handleAttestationUrlInput = (attestationLabel: string, url: string | null) => {
    setAttestationUrls(prevUrls => {
      const newUrls = new Map(prevUrls)
      if (url && url.trim()) {
        newUrls.set(attestationLabel, url.trim())
        // Clear file if URL is being set
        setAttestationFiles(prevFiles => {
          const newFiles = new Map(prevFiles)
          newFiles.delete(attestationLabel)
          return newFiles
        })
        // Auto-check subprocessor if DPA URL is provided
        if (attestationLabel === 'DPA') {
          setFormData(prevData => ({ ...prevData, isSubprocessor: true }))
        }
      } else {
        newUrls.delete(attestationLabel)
        // Uncheck subprocessor if DPA URL is removed
        if (attestationLabel === 'DPA') {
          setFormData(prevData => ({ ...prevData, isSubprocessor: false }))
        }
      }
      return newUrls
    })
  }

  const analyzeVendorForAttestations = async () => {
    // Abort previous analysis if it exists
    if (analyzeVendorAbortControllerRef.current) {
      analyzeVendorAbortControllerRef.current.abort()
    }

    // Create new AbortController for this analysis
    const controller = new AbortController()
    analyzeVendorAbortControllerRef.current = controller

    setAnalyzingVendor(true)

    try {
      // Build submission data (without actually submitting)
      let additionalContext = formData.questionnaire.additionalContext
      const overrideReasons = Object.entries(answerOverrides)
        .filter(([_, reason]) => reason)
        .map(([key, reason]) => {
          const questionLabels = {
            accessCustomerData: 'Customer Data Access',
            accessEmployeePII: 'Employee PII Access',
            accessInternalData: 'Internal Data Access'
          }
          return `[AI Override - ${questionLabels[key as keyof typeof questionLabels]}]: ${reason}`
        })

      if (overrideReasons.length > 0) {
        additionalContext = overrideReasons.join('\n') + (additionalContext ? '\n\n' + additionalContext : '')
      }

      const analysisData = {
        ...formData,
        questionnaire: {
          ...formData.questionnaire,
          additionalContext
        },
        analyzeOnly: true // Tell backend to only analyze, not save
      }

      console.log('[Vendor Analysis] Analyzing for attestations with questionnaire:', {
        accessCustomerData: analysisData.questionnaire.accessCustomerData,
        accessEmployeePII: analysisData.questionnaire.accessEmployeePII,
        accessInternalData: analysisData.questionnaire.accessInternalData
      })

      const response = await fetchWithAuth('/api/vendor-risk-assessment-analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisData),
        signal: controller.signal
      })

      if (!response.ok) {
        throw new Error('Failed to analyze vendor')
      }

      const result = await response.json()

      // Check if request was aborted before updating state
      if (controller.signal.aborted) {
        console.log('Vendor analysis was aborted')
        return
      }

      // If there are attestations to confirm, show step 5
      if (result.attestationsToConfirm && result.attestationsToConfirm.length > 0) {
        console.log('[Vendor Analysis] Attestations to confirm:', result.attestationsToConfirm)
        console.log('[Vendor Analysis] Data classification:', result.dataClassification)
        console.log('[Vendor Analysis] DPA attestation details:', result.attestationsToConfirm.find((a: any) => a.id === 'hasDpa'))
        setUncertainAttestations(result.attestationsToConfirm)
        setFormStep(5)
      } else {
        // No attestations to confirm, proceed to final submit
        await submitFinalAssessment([])
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Vendor analysis request was aborted')
        return
      }
      console.error('Error analyzing vendor:', error)
      if (!controller.signal.aborted) {
        alert('Failed to analyze vendor. Please try again.')
      }
    } finally {
      if (!controller.signal.aborted) {
        setAnalyzingVendor(false)
      }
    }
  }

  const submitFinalAssessment = async (confirmedAttestations: string[]) => {
    console.log('[Vendor Assessment DEBUG] === Starting Final Submission ===')
    console.log('[Vendor Assessment DEBUG] Confirmed attestations:', confirmedAttestations)
    console.log('[Vendor Assessment DEBUG] Uncertain attestations presented:', uncertainAttestations)
    console.log('[Vendor Assessment DEBUG] Attestation files:', Array.from(attestationFiles.entries()).map(([k, v]) => ({ label: k, filename: v.name })))
    console.log('[Vendor Assessment DEBUG] Attestation URLs:', Array.from(attestationUrls.entries()))

    setIsSubmitting(true)

    try {
      // Log attestation status for debugging (validation removed - users can submit without required docs)
      console.log('[Vendor Assessment DEBUG] Checking attestation status:')
      uncertainAttestations.forEach(attestation => {
        console.log('[Vendor Assessment DEBUG] Attestation:', {
          label: attestation.label,
          required: attestation.required,
          isConfirmed: confirmedAttestations.includes(attestation.label),
          hasFile: attestationFiles.has(attestation.label),
          hasUrl: attestationUrls.has(attestation.label)
        })
      })

      console.log('[Vendor Assessment DEBUG] Proceeding with submission (required documents validation removed - backend will set appropriate status)')
      // Upload file first if present
      let uploadedFileMetadata = null
      if (uploadedFile) {
        setUploadingFile(true)
        try {
          const fileFormData = new FormData()
          fileFormData.append('file', uploadedFile)
          fileFormData.append('vendorName', formData.vendorName)

          const fileUploadResponse = await fetchWithAuth('/api/vendor-risk-assessment-upload', {
            method: 'POST',
            body: fileFormData
          })

          if (fileUploadResponse.ok) {
            const fileResult = await fileUploadResponse.json()
            uploadedFileMetadata = fileResult.file
          } else {
            console.error('File upload failed:', await fileUploadResponse.text())
            // Continue anyway - file upload is optional
          }
        } catch (error) {
          console.error('Error uploading file:', error)
          // Continue anyway - file upload is optional
        } finally {
          setUploadingFile(false)
        }
      }

      // Build submission data with confirmed attestations
      let additionalContext = formData.questionnaire.additionalContext
      const overrideReasons = Object.entries(answerOverrides)
        .filter(([_, reason]) => reason)
        .map(([key, reason]) => {
          const questionLabels = {
            accessCustomerData: 'Customer Data Access',
            accessEmployeePII: 'Employee PII Access',
            accessInternalData: 'Internal Data Access'
          }
          return `[AI Override - ${questionLabels[key as keyof typeof questionLabels]}]: ${reason}`
        })

      if (overrideReasons.length > 0) {
        additionalContext = overrideReasons.join('\n') + (additionalContext ? '\n\n' + additionalContext : '')
      }

      // NEW: Build confirmed attestation IDs array from the uncertain attestations
      const confirmedAttestationIds = confirmedAttestations.map(label => {
        const attestation = uncertainAttestations.find(a => a.label === label)
        return attestation?.id
      }).filter(Boolean) as string[]

      console.log('[Vendor Assessment DEBUG] Mapped confirmed attestations to IDs:', confirmedAttestationIds)

      // NEW: Build attestationArtifacts object from Maps
      console.log('[Vendor Assessment DEBUG] Building attestationArtifacts object...')
      const attestationArtifacts: { [key: string]: { referenceUrl?: string; uploadedFileName?: string; uploadedFileKey?: string } } = {}

      // First, upload all attestation files to blob storage (concurrently)
      const uploadPromises = Array.from(attestationFiles.entries()).map(async ([label, file]) => {
        const attestation = uncertainAttestations.find(a => a.label === label)
        if (!attestation) return { label, success: false }

        console.log(`[Vendor Assessment DEBUG] Uploading attestation file for ${label}:`, file.name)

        try {
          const attestationFileFormData = new FormData()
          attestationFileFormData.append('file', file)
          attestationFileFormData.append('vendorName', formData.vendorName)
          attestationFileFormData.append('attestationId', attestation.id)

          const attestationUploadResponse = await fetchWithAuth('/api/vendor-risk-assessment-upload', {
            method: 'POST',
            body: attestationFileFormData
          })

          if (attestationUploadResponse.ok) {
            const attestationFileResult = await attestationUploadResponse.json()
            console.log(`[Vendor Assessment DEBUG] Uploaded attestation file for ${label}:`, attestationFileResult)

            return {
              label,
              success: true,
              attestationId: attestation.id,
              result: {
                uploadedFileName: attestationFileResult.file.filename,
                uploadedFileKey: attestationFileResult.file.key
              }
            }
          } else {
            console.error(`[Vendor Assessment DEBUG] Failed to upload attestation file for ${label}:`, await attestationUploadResponse.text())
            return { label, success: false }
          }
        } catch (error) {
          console.error(`[Vendor Assessment DEBUG] Error uploading attestation file for ${label}:`, error)
          return { label, success: false }
        }
      })

      const uploadResults = await Promise.all(uploadPromises)

      // Add successful uploads to attestationArtifacts
      uploadResults.forEach(result => {
        if (result.success && result.attestationId && result.result) {
          attestationArtifacts[result.attestationId] = result.result
        }
      })

      // Next, add reference URLs to attestationArtifacts
      for (const [label, url] of attestationUrls.entries()) {
        const attestation = uncertainAttestations.find(a => a.label === label)
        if (!attestation) continue

        console.log(`[Vendor Assessment DEBUG] Adding reference URL for ${label}:`, url)

        if (attestationArtifacts[attestation.id]) {
          // File already exists, add URL to it
          attestationArtifacts[attestation.id].referenceUrl = url
        } else {
          // No file, just URL
          attestationArtifacts[attestation.id] = {
            referenceUrl: url
          }
        }
      }

      console.log('[Vendor Assessment DEBUG] Final attestationArtifacts:', attestationArtifacts)

      const submissionData = {
        ...formData,
        questionnaire: {
          ...formData.questionnaire,
          additionalContext
        },
        confirmedAttestations, // Keep for backward compatibility
        confirmedAttestationIds, // NEW: Send IDs for proper mapping
        uploadedFile: uploadedFileMetadata,
        attestationArtifacts, // NEW: Send artifact metadata
        // If re-assessing, include the ID of the old assessment to archive
        editingFromId: editingVendor?.id
      }

      const response = await fetchWithAuth('/api/vendor-risk-assessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData)
      })

      if (!response.ok) throw new Error('Failed to submit vendor assessment')

      const result = await response.json()

      console.log('[Vendor Assessment DEBUG] Backend response:', result)
      console.log('[Vendor Assessment DEBUG] Assessment status:', result.assessment?.status)
      console.log('[Vendor Assessment DEBUG] Full assessment:', result.assessment)

      // Add to vendors list, and if re-assessing, mark the old vendor as archived in local state
      if (editingVendor) {
        // Update the old vendor to be archived in local state
        setVendors([
          result.assessment,
          ...vendors.map(v =>
            v.id === editingVendor.id
              ? { ...v, archived: true, archivedAt: new Date().toISOString(), archivedBy: result.assessment.submittedBy }
              : v
          )
        ])
      } else {
        setVendors([result.assessment, ...vendors])
      }

      // Reset form
      setFormData({
        vendorName: '',
        websiteUrl: '',
        questionnaire: {
          servicePurpose: '',
          accessCustomerData: null,
          accessEmployeePII: null,
          accessInternalData: null,
          dataTypes: [],
          additionalContext: ''
        }
      })
      setFormStep(1)
      setShowForm(false)
      setEditingVendor(null)
      setAiSuggestedAnswers(null)
      setAnswerOverrides({})
      setSuggestedClassification(null)
      setUncertainAttestations([])
      setConfirmedAttestations([])
      setAttestationFiles(new Map())
      setAttestationUrls(new Map())
      setUploadedFile(null)

      // If vendor requires exception, prompt immediately
      console.log('[Vendor Assessment DEBUG] Checking if exception required...')
      console.log('[Vendor Assessment DEBUG] Status check:', result.assessment.status, '===', 'exception-required', '?', result.assessment.status === 'exception-required')

      if (result.assessment.status === 'exception-required') {
        console.log('[Vendor Assessment DEBUG] ✅ Opening exception modal!')
        // Switch to assessments tab to show the new vendor
        setActiveTab('assessments')
        // Small delay to ensure UI updates, then open exception modal
        setTimeout(() => {
          console.log('[Vendor Assessment DEBUG] ⏰ Timeout fired, setting modal state...')
          setExceptionVendor(result.assessment)
          setExceptionReason('')
          setExceptionScreenshot(null)
          setShowExceptionModal(true)
        }, 300)
      } else {
        console.log('[Vendor Assessment DEBUG] ❌ Not opening exception modal, status is:', result.assessment.status)
      }
    } catch (error) {
      console.error('Error submitting vendor:', error)
      alert('Failed to submit vendor assessment. Please try again.')
    } finally {
      setIsSubmitting(false)
      setUploadingFile(false)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type (accept common document formats)
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv'
    ]

    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a PDF, Word document, Excel spreadsheet, or text file')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB')
      return
    }

    setUploadedFile(file)
  }

  const handleSubmitException = async () => {
    if (!exceptionVendor || !exceptionReason.trim() || !exceptionScreenshot) {
      alert('Please provide both a reason and upload a screenshot of manager approval')
      return
    }

    setSubmittingException(true)
    try {
      const formData = new FormData()
      formData.append('assessmentId', exceptionVendor.id)
      formData.append('reason', exceptionReason)
      formData.append('screenshot', exceptionScreenshot)

      const response = await fetchWithAuth('/api/vendor-exception-request', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to submit exception request')
      }

      const result = await response.json()

      // Update vendor in local state
      setVendors(vendors.map(v => v.id === exceptionVendor.id ? result.assessment : v))

      // Close modal and reset form
      setShowExceptionModal(false)
      setExceptionVendor(null)
      setExceptionReason('')
      setExceptionScreenshot(null)

      alert('Exception request submitted successfully! Your security team will review it shortly.')
    } catch (error) {
      console.error('Error submitting exception:', error)
      alert('Failed to submit exception request. Please try again.')
    } finally {
      setSubmittingException(false)
    }
  }

  const handleSubmitLater = async () => {
    if (!exceptionVendor) {
      return
    }

    console.log('[Submit Later] Starting status update for vendor:', exceptionVendor.vendorName)
    console.log('[Submit Later] Current vendor status:', exceptionVendor.status)
    console.log('[Submit Later] Vendor ID:', exceptionVendor.id)

    setSubmittingException(true)
    try {
      // Update vendor status from 'exception-required' to 'manager-approval' (pending state)
      const updatePayload = {
        assessmentId: exceptionVendor.id,
        updates: {
          status: 'manager-approval'
        }
      }
      console.log('[Submit Later] Sending PATCH request with payload:', updatePayload)

      const response = await fetchWithAuth('/api/vendor-risk-assessment-update', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload),
        credentials: 'include'
      })

      console.log('[Submit Later] Response status:', response.status, response.statusText)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('[Submit Later] API error response:', errorText)
        throw new Error('Failed to update vendor status')
      }

      const result = await response.json()

      console.log('[Submit Later] API response:', result)
      console.log('[Submit Later] Updated assessment status:', result.assessment?.status)
      console.log('[Submit Later] Full updated assessment:', result.assessment)

      // Update vendor in local state with functional updater
      console.log('[Submit Later] Updating local state with new assessment')
      setVendors(prev => prev.map(v => v.id === exceptionVendor.id ? result.assessment : v))
      console.log('[Submit Later] State updated successfully')

      // Close modal and reset form
      setShowExceptionModal(false)
      setExceptionVendor(null)
      setExceptionReason('')
      setExceptionScreenshot(null)

      // Success message
      alert('Vendor saved with pending status. You can submit an exception request later if needed.')
    } catch (error) {
      console.error('Error updating vendor status:', error)
      alert('Failed to update vendor status. Please try again.')
    } finally {
      setSubmittingException(false)
    }
  }

  const handleReviewException = async (vendorId: string, decision: 'approved' | 'rejected') => {
    if (!reviewComments.trim() && decision === 'rejected') {
      alert('Please provide comments explaining why the exception is being rejected')
      return
    }

    setSubmittingReview(true)
    try {
      const response = await fetchWithAuth('/api/vendor-exception-request', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assessmentId: vendorId,
          decision,
          comments: reviewComments
        }),
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Failed to review exception')
      }

      const result = await response.json()

      // Update vendor in local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))

      // Reset review form
      setReviewingVendorId(null)
      setReviewComments('')

      alert(`Exception ${decision} successfully!`)
    } catch (error) {
      console.error('Error reviewing exception:', error)
      alert('Failed to review exception. Please try again.')
    } finally {
      setSubmittingReview(false)
    }
  }

  const removeUploadedFile = () => {
    setUploadedFile(null)
  }

  const handleCsvUpload = async () => {
    if (!csvFile) {
      alert('Please select a CSV file to upload')
      return
    }

    setUploadingCsv(true)
    const errors: string[] = []
    let successCount = 0

    try {
      // Read CSV file
      const text = await csvFile.text()
      const lines = text.split('\n').map(line => line.trim()).filter(line => line)

      if (lines.length < 2) {
        alert('CSV file must contain a header row and at least one data row')
        return
      }

      // Parse header
      const headerLine = lines[0]
      const headers = headerLine.split(',').map(h => h.trim().replace(/^"|"$/g, ''))

      // Find column indices (case insensitive)
      const vendorNameIdx = headers.findIndex(h => h.toLowerCase() === 'vendor name' || h.toLowerCase() === 'vendorname')
      const websiteIdx = headers.findIndex(h => h.toLowerCase() === 'website' || h.toLowerCase() === 'website url' || h.toLowerCase() === 'url')
      const servicePurposeIdx = headers.findIndex(h => h.toLowerCase() === 'service purpose' || h.toLowerCase() === 'description')
      const purposeIdx = headers.findIndex(h => h.toLowerCase() === 'purpose' || h.toLowerCase() === '3-word purpose')
      const isSubprocessorIdx = headers.findIndex(h => h.toLowerCase() === 'is subprocessor' || h.toLowerCase() === 'subprocessor')
      const companyOwnerIdx = headers.findIndex(h => h.toLowerCase() === 'company owner' || h.toLowerCase() === 'owner')
      const vendorEmailIdx = headers.findIndex(h => h.toLowerCase() === 'vendor email' || h.toLowerCase() === 'vendor email address' || h.toLowerCase() === 'email')
      const isHIPAAIdx = headers.findIndex(h => h.toLowerCase() === 'hipaa' || h.toLowerCase() === 'is hipaa')
      const hasBAAIdx = headers.findIndex(h => h.toLowerCase() === 'baa' || h.toLowerCase() === 'has baa')
      const dataClassificationIdx = headers.findIndex(h => h.toLowerCase() === 'data classification' || h.toLowerCase() === 'classification' || h.toLowerCase() === 'data class')

      if (vendorNameIdx === -1) {
        alert('CSV must contain a "Vendor Name" column')
        return
      }

      // Process data rows
      const dataLines = lines.slice(1)
      setCsvProcessingProgress({current: 0, total: dataLines.length})

      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i]

        // Simple CSV parser (handles quoted fields)
        const fields: string[] = []
        let currentField = ''
        let insideQuotes = false

        for (let j = 0; j < line.length; j++) {
          const char = line[j]
          if (char === '"') {
            insideQuotes = !insideQuotes
          } else if (char === ',' && !insideQuotes) {
            fields.push(currentField.trim())
            currentField = ''
          } else {
            currentField += char
          }
        }
        fields.push(currentField.trim()) // Add last field

        const vendorName = fields[vendorNameIdx]?.replace(/^"|"$/g, '').trim()

        if (!vendorName) {
          errors.push(`Row ${i + 2}: Missing vendor name`)
          setCsvProcessingProgress({current: i + 1, total: dataLines.length})
          continue
        }

        // Auto-generate website URL from vendor name if not provided
        const generateWebsiteUrl = (name: string): string => {
          // Remove common suffixes and special characters
          let domain = name.toLowerCase()
            .replace(/\s+(inc\.?|llc|ltd\.?|corporation|corp\.?)$/i, '')
            .replace(/[^\w\s-]/g, '')  // Remove special chars except spaces and hyphens
            .replace(/\s+/g, '')       // Remove spaces
            .replace(/-+/g, '')        // Remove hyphens
          return `https://www.${domain}.com`
        }

        const providedWebsite = websiteIdx !== -1 ? fields[websiteIdx]?.replace(/^"|"$/g, '').trim() : ''
        const website = providedWebsite || generateWebsiteUrl(vendorName)
        const servicePurpose = servicePurposeIdx !== -1 ? fields[servicePurposeIdx]?.replace(/^"|"$/g, '').trim() : 'No description provided'
        const isSubprocessorValue = isSubprocessorIdx !== -1 ? fields[isSubprocessorIdx]?.replace(/^"|"$/g, '').trim().toLowerCase() : 'no'
        const isSubprocessor = isSubprocessorValue === 'yes' || isSubprocessorValue === 'y' || isSubprocessorValue === 'true' || isSubprocessorValue === '1'

        // Extract new fields
        const companyOwner = companyOwnerIdx !== -1 ? fields[companyOwnerIdx]?.replace(/^"|"$/g, '').trim() : undefined
        const vendorEmail = vendorEmailIdx !== -1 ? fields[vendorEmailIdx]?.replace(/^"|"$/g, '').trim() : undefined
        const isHIPAAValue = isHIPAAIdx !== -1 ? fields[isHIPAAIdx]?.replace(/^"|"$/g, '').trim().toLowerCase() : 'no'
        const isHIPAA = isHIPAAValue === 'yes' || isHIPAAValue === 'y' || isHIPAAValue === 'true' || isHIPAAValue === '1'
        const hasBAAValue = hasBAAIdx !== -1 ? fields[hasBAAIdx]?.replace(/^"|"$/g, '').trim().toLowerCase() : 'no'
        const hasBAA = hasBAAValue === 'yes' || hasBAAValue === 'y' || hasBAAValue === 'true' || hasBAAValue === '1'

        // Extract and validate data classification
        let dataClassification: 'L0' | 'L1' | 'L2' | 'L3' | undefined = undefined
        if (dataClassificationIdx !== -1) {
          const classValue = fields[dataClassificationIdx]?.replace(/^"|"$/g, '').trim().toUpperCase()
          if (classValue === 'L0' || classValue === 'L1' || classValue === 'L2' || classValue === 'L3') {
            dataClassification = classValue as 'L0' | 'L1' | 'L2' | 'L3'
          } else if (classValue) {
            // Invalid classification provided
            errors.push(`Row ${i + 2} (${vendorName}): Invalid data classification "${classValue}". Must be L0, L1, L2, or L3.`)
            setCsvProcessingProgress({current: i + 1, total: dataLines.length})
            continue
          }
        }

        // Extract and validate purpose (max 3 words)
        let purpose: string | undefined = undefined
        if (purposeIdx !== -1) {
          const purposeValue = fields[purposeIdx]?.replace(/^"|"$/g, '').trim()
          if (purposeValue) {
            const purposeWords = purposeValue.split(/\s+/)
            if (purposeWords.length > 3) {
              errors.push(`Row ${i + 2} (${vendorName}): Purpose must be 3 words or less. Got ${purposeWords.length} words.`)
              setCsvProcessingProgress({current: i + 1, total: dataLines.length})
              continue
            }
            purpose = purposeValue
          }
        }

        try {
          const response = await fetchWithAuth('/api/vendor-risk-assessment-bulk-add', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
              vendorName,
              websiteUrl: website,
              companyOwner,
              vendorEmail,
              isSubprocessor,
              isHIPAA,
              hasBAA,
              dataClassification,
              purpose,
              questionnaire: {
                servicePurpose,
                accessCustomerData: null,
                accessEmployeePII: null,
                accessInternalData: null,
                dataTypes: [],
                additionalContext: ''
              }
            })
          })

          if (!response.ok) {
            const errorData = await response.json()
            if (response.status === 409) {
              errors.push(`Row ${i + 2} (${vendorName}): Already has an active assessment`)
            } else {
              errors.push(`Row ${i + 2} (${vendorName}): ${errorData.error || 'Failed to add vendor'}`)
            }
          } else {
            successCount++
          }
        } catch (error) {
          errors.push(`Row ${i + 2} (${vendorName}): Network error - ${error instanceof Error ? error.message : 'Unknown error'}`)
        }

        setCsvProcessingProgress({current: i + 1, total: dataLines.length})
      }

      // Update results
      setCsvUploadResults({success: successCount, errors})

      // If at least one vendor was successfully added, refresh the vendor list
      if (successCount > 0) {
        const vendorsResponse = await fetchWithAuth('/api/vendor-risk-assessment', {
          credentials: 'include'
        })
        if (vendorsResponse.ok) {
          const vendorsData = await vendorsResponse.json()
          setVendors(vendorsData)
        }
      }

    } catch (error) {
      console.error('Error parsing CSV:', error)
      alert('Failed to parse CSV file. Please ensure it is properly formatted.')
    } finally {
      setUploadingCsv(false)
    }
  }

  // Bulk AI Generation Handler
  const handleBulkGenerate = async () => {
    if (selectedVendorIds.size === 0) return

    console.log('[AI Generate] Total vendors in state:', vendors.length)
    console.log('[AI Generate] Deleted vendors in state:', vendors.filter((v: any) => v.deleted).length)
    console.log('[AI Generate] Selected vendor IDs:', Array.from(selectedVendorIds))

    // Filter out deleted and archived vendors before generating
    const activeVendorIds = Array.from(selectedVendorIds).filter(id => {
      const vendor = vendors.find(v => v.id === id)
      console.log('[AI Generate] Checking vendor ID:', id, 'Found:', !!vendor, 'Deleted:', vendor?.deleted, 'Archived:', vendor?.archived)
      return vendor && !vendor.deleted && !vendor.archived
    })

    console.log('[AI Generate] Active vendor IDs after filtering:', activeVendorIds)

    if (activeVendorIds.length === 0) {
      setGenerateResults({
        success: 0,
        errors: ['Selected vendors are deleted or archived. Please select active vendors.']
      })
      return
    }

    setGeneratingContent(true)
    setGenerateProgress({ current: 0, total: activeVendorIds.length })
    setGenerateResults({ success: 0, errors: [] })

    try {
      const assessmentIds = activeVendorIds

      const response = await fetchWithAuth('/api/vendor-risk-assessment-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assessmentIds,
          generateDescription,
          generatePurpose
        })
      })

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please refresh the page and log in again.')
        }
        throw new Error('Failed to generate content')
      }

      const result = await response.json()

      setGenerateResults({
        success: result.summary.successful,
        errors: result.errors.map((e: any) =>
          `${e.vendorName || e.assessmentId}: ${e.error}`
        )
      })

      // Refresh vendor list to show updated data
      const vendorsResponse = await fetchWithAuth('/api/vendor-risk-assessment', {
        credentials: 'include'
      })
      if (vendorsResponse.ok) {
        const vendorsData = await vendorsResponse.json()
        setVendors(vendorsData)
      }

      // Clear selections after successful generation
      if (result.summary.successful > 0) {
        setSelectedVendorIds(new Set())
      }

    } catch (error) {
      console.error('Error generating content:', error)
      setGenerateResults({
        success: 0,
        errors: ['Failed to generate content. Please try again.']
      })
    } finally {
      setGeneratingContent(false)
    }
  }

  // Quick Edit Mode Handlers
  const handleQuickEditClassification = async (vendorId: string, newClassification: 'L0' | 'L1' | 'L2' | 'L3') => {
    try {
      // Find the vendor
      const vendor = vendors.find(v => v.id === vendorId)
      if (!vendor) return

      // Update via API (we'll need to create this endpoint)
      const response = await fetchWithAuth('/api/vendor-risk-assessment-update', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assessmentId: vendorId,
          updates: {
            dataClassification: newClassification
          }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update classification')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))

      // Clear editing state for this vendor
      setEditingClassification(prev => {
        const next = { ...prev }
        delete next[vendorId]
        return next
      })
    } catch (error) {
      console.error('Error updating classification:', error)
      alert('Failed to update classification. Please try again.')
    }
  }

  const handleQuickEditUploadDoc = async (vendorId: string, docType: string, file: File) => {
    try {
      const formData = new FormData()
      formData.append('assessmentId', vendorId)
      formData.append('docType', docType)
      formData.append('file', file)

      const response = await fetchWithAuth('/api/vendor-risk-assessment-upload-doc', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      if (!response.ok) {
        throw new Error('Failed to upload document')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v => v.id === vendorId ? result.assessment : v))

      // Clear uploading state
      setUploadingDocs(prev => {
        const next = { ...prev }
        delete next[vendorId]
        return next
      })

      alert(`${docType} uploaded successfully!`)
    } catch (error) {
      console.error('Error uploading document:', error)
      alert('Failed to upload document. Please try again.')
    }
  }

  // Multi-select handlers
  const toggleVendorSelection = (vendorId: string) => {
    setSelectedVendorIds(prev => {
      const next = new Set(prev)
      if (next.has(vendorId)) {
        next.delete(vendorId)
      } else {
        next.add(vendorId)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedVendorIds.size === filteredVendors.length) {
      // Deselect all
      setSelectedVendorIds(new Set())
    } else {
      // Select all filtered vendors
      setSelectedVendorIds(new Set(filteredVendors.map(v => v.id)))
    }
  }

  const handleBulkArchive = async () => {
    if (selectedVendorIds.size === 0) return

    const count = selectedVendorIds.size
    if (!confirm(`Archive ${count} vendor assessment${count > 1 ? 's' : ''}?`)) {
      return
    }

    try {
      const promises = Array.from(selectedVendorIds).map(async (vendorId) => {
        const vendor = vendors.find(v => v.id === vendorId)
        if (!vendor) return

        const response = await fetchWithAuth('/api/vendor-risk-assessment-archive', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ assessmentId: vendorId })
        })

        if (!response.ok) {
          throw new Error(`Failed to archive ${vendor.vendorName}`)
        }

        return response.json()
      })

      await Promise.all(promises)

      // Update local state - mark as archived
      setVendors(vendors.map(v =>
        selectedVendorIds.has(v.id)
          ? { ...v, archived: true }
          : v
      ))

      // Clear selection
      setSelectedVendorIds(new Set())

      alert(`Successfully archived ${count} vendor${count > 1 ? 's' : ''}`)
    } catch (error) {
      console.error('Error bulk archiving:', error)
      alert('Failed to archive some vendors. Please try again.')
    }
  }

  const handleBulkMoveToRecycleBin = async () => {
    if (selectedVendorIds.size === 0) return

    const count = selectedVendorIds.size
    if (!confirm(`Move ${count} vendor assessment${count > 1 ? 's' : ''} to recycle bin?`)) {
      return
    }

    try {
      const promises = Array.from(selectedVendorIds).map(async (vendorId) => {
        const vendor = vendors.find(v => v.id === vendorId)
        if (!vendor) return

        const response = await fetchWithAuth('/api/vendor-risk-assessment-delete', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ assessmentId: vendorId })
        })

        if (!response.ok) {
          throw new Error(`Failed to delete ${vendor.vendorName}`)
        }

        return response.json()
      })

      await Promise.all(promises)

      // Update local state - mark as deleted
      setVendors(vendors.map(v =>
        selectedVendorIds.has(v.id)
          ? { ...v, deleted: true }
          : v
      ))

      // Clear selection
      setSelectedVendorIds(new Set())

      alert(`Successfully moved ${count} vendor${count > 1 ? 's' : ''} to recycle bin`)
    } catch (error) {
      console.error('Error bulk deleting:', error)
      alert('Failed to delete some vendors. Please try again.')
    }
  }

  const handleToggleSubprocessor = async (vendorId: string, currentIsSubprocessor: boolean) => {
    try {
      const response = await fetchWithAuth('/api/vendor-risk-assessment-update', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          assessmentId: vendorId,
          updates: { isSubprocessor: !currentIsSubprocessor }
        })
      })

      if (!response.ok) {
        throw new Error('Failed to toggle subprocessor status')
      }

      const result = await response.json()

      // Update local state
      setVendors(vendors.map(v =>
        v.id === vendorId
          ? { ...v, isSubprocessor: !currentIsSubprocessor }
          : v
      ))
    } catch (error) {
      console.error('Error toggling subprocessor:', error)
      alert('Failed to update subprocessor status. Please try again.')
    }
  }

  const handleDiscoverVendor = async (urlOverride?: string) => {
    // Use override URL if provided (from Enter key), otherwise use formData
    const websiteUrl = urlOverride || formData.websiteUrl

    if (!websiteUrl) {
      alert('Please enter website URL first')
      return
    }

    // Extract vendor name from URL if not provided (for Step 0 auto-discovery)
    let vendorName = formData.vendorName
    if (!vendorName) {
      try {
        const url = new URL(websiteUrl)
        // Extract domain without TLD: "zendesk.com" -> "Zendesk"
        const domain = url.hostname.replace('www.', '').split('.')[0]
        vendorName = domain.charAt(0).toUpperCase() + domain.slice(1)
        console.log('[Vendor Discovery] Extracted vendor name from URL:', vendorName)
      } catch (e) {
        console.error('[Vendor Discovery] Failed to parse URL:', websiteUrl, e)
        vendorName = 'Unknown Vendor'
      }
    }

    setDiscovering(true)
    try {
      const response = await fetchWithAuth('/api/vendor-risk-assessment-discover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vendorName: vendorName,
          websiteUrl: websiteUrl
        })
      })

      const result = await response.json()

      if (!response.ok) {
        // Log detailed error information
        console.error('[Vendor Discovery] Response error:', result)
        const errorMessage = result.error || 'Failed to discover vendor information'
        const debugInfo = result.debug ? `\n\nDebug Info:\n${JSON.stringify(result.debug, null, 2)}` : ''
        const errorDetails = result.details ? `\n\nDetails: ${result.details}` : ''
        throw new Error(errorMessage + errorDetails + debugInfo)
      }

      // Store AI suggested answers
      if (result.suggestedAnswers) {
        const suggestions: any = {}
        if (result.suggestedAnswers.accessCustomerData !== undefined) {
          suggestions.accessCustomerData = {
            suggested: result.suggestedAnswers.accessCustomerData,
            reasoning: result.suggestedAnswers.accessCustomerDataReasoning || ''
          }
        }
        if (result.suggestedAnswers.accessEmployeePII !== undefined) {
          suggestions.accessEmployeePII = {
            suggested: result.suggestedAnswers.accessEmployeePII,
            reasoning: result.suggestedAnswers.accessEmployeePIIReasoning || ''
          }
        }
        if (result.suggestedAnswers.accessInternalData !== undefined) {
          suggestions.accessInternalData = {
            suggested: result.suggestedAnswers.accessInternalData,
            reasoning: result.suggestedAnswers.accessInternalDataReasoning || ''
          }
        }
        setAiSuggestedAnswers(suggestions)

        // Auto-populate form with AI suggestions - populate purpose and description
        console.log('[Query Vendor] API Response:', {
          servicePurpose: result.servicePurpose,
          serviceDescription: result.serviceDescription,
          willSetPurposeTo: result.servicePurpose || formData.purpose,
          willSetQuestionnaireServicePurposeTo: result.servicePurpose || formData.questionnaire.servicePurpose,
          vendorName: vendorName
        })
        setFormData({
          ...formData,
          websiteUrl: websiteUrl, // Use normalized URL from parameter
          vendorName: vendorName, // Set extracted vendor name
          purpose: result.servicePurpose || formData.purpose,
          aiSummary: result.serviceDescription || formData.aiSummary,
          questionnaire: {
            ...formData.questionnaire,
            servicePurpose: result.servicePurpose || formData.questionnaire.servicePurpose,
            accessCustomerData: suggestions.accessCustomerData?.suggested ?? null,
            accessEmployeePII: suggestions.accessEmployeePII?.suggested ?? null,
            accessInternalData: suggestions.accessInternalData?.suggested ?? null
          }
        })
      } else {
        // Fallback if no suggested answers - populate purpose and description
        setFormData({
          ...formData,
          websiteUrl: websiteUrl, // Use normalized URL from parameter
          vendorName: vendorName, // Set extracted vendor name
          purpose: result.servicePurpose || formData.purpose,
          aiSummary: result.serviceDescription || formData.aiSummary,
          questionnaire: {
            ...formData.questionnaire,
            servicePurpose: result.servicePurpose || formData.questionnaire.servicePurpose
          }
        })
      }

      // Store suggested classification
      if (result.suggestedClassification && result.classificationReasoning) {
        setSuggestedClassification({
          level: result.suggestedClassification,
          reasoning: result.classificationReasoning
        })
      }

      // Show success message
      alert(`AI Discovery Complete!\n\nGenerated service description, suggested data classification, and auto-populated questionnaire answers. You can review and modify before proceeding.`)
    } catch (error) {
      console.error('Error discovering vendor:', error)
      alert('Failed to discover vendor information. Please try again or enter details manually.')
    } finally {
      setDiscovering(false)
    }
  }

  const handleAnswerChange = (
    questionKey: 'accessCustomerData' | 'accessEmployeePII' | 'accessInternalData',
    newValue: boolean
  ) => {
    const aiSuggestion = aiSuggestedAnswers?.[questionKey]

    // Check if this differs from AI suggestion
    if (aiSuggestion && aiSuggestion.suggested !== newValue && !answerOverrides[questionKey]) {
      const reason = prompt(
        `You're changing the AI-suggested answer for this question.\n\n` +
        `AI suggested: ${aiSuggestion.suggested ? 'Yes' : 'No'}\n` +
        `Reasoning: ${aiSuggestion.reasoning}\n\n` +
        `Please provide a brief reason for this override:`
      )

      if (reason && reason.trim()) {
        setAnswerOverrides({
          ...answerOverrides,
          [questionKey]: reason.trim()
        })
        setFormData({
          ...formData,
          questionnaire: {
            ...formData.questionnaire,
            [questionKey]: newValue
          }
        })
      } else if (reason === null) {
        // User cancelled - don't change the answer
        return
      } else {
        // Empty reason provided
        alert('Please provide a reason for overriding the AI suggestion, or click Cancel to keep the suggested answer.')
        return
      }
    } else {
      // No AI suggestion or same as suggestion - just update normally
      setFormData({
        ...formData,
        questionnaire: {
          ...formData.questionnaire,
          [questionKey]: newValue
        }
      })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // From step 4, we need to analyze for uncertain attestations first
    await analyzeVendorForAttestations()
  }

  const getStatusBadge = (status: VendorAssessment['status']) => {
    switch (status) {
      case 'auto-approved':
        return (
          <div className="flex items-center gap-2 text-green-700 bg-green-100 px-3 py-1.5 rounded-full text-sm font-semibold border-2 border-green-600">
            <CheckCircle className="h-5 w-5" />
            <span>Approved - Ready to Use</span>
          </div>
        )
      case 'manager-approval':
        return (
          <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 px-3 py-1 rounded-full text-sm font-medium">
            <AlertTriangle className="h-4 w-4" />
            Pending
          </div>
        )
      case 'exception-required':
        return (
          <div className="flex items-center gap-2 text-orange-700 bg-orange-50 px-3 py-1 rounded-full text-sm font-medium">
            <AlertTriangle className="h-4 w-4" />
            Exception Required
          </div>
        )
      case 'exception-approved':
        return (
          <div className="flex items-center gap-2 text-green-700 bg-green-100 px-3 py-1.5 rounded-full text-sm font-semibold border-2 border-green-600">
            <CheckCircle className="h-5 w-5" />
            <span>Exception Approved - Ready to Use</span>
          </div>
        )
      case 'exception-rejected':
        return (
          <div className="flex items-center gap-2 text-red-700 bg-red-50 px-3 py-1 rounded-full text-sm font-medium">
            <XCircle className="h-4 w-4" />
            Exception Rejected
          </div>
        )
      case 'analyzing':
        return (
          <div className="flex items-center gap-2 text-blue-700 bg-blue-50 px-3 py-1 rounded-full text-sm font-medium">
            <Loader2 className="h-4 w-4 animate-spin" />
            Analyzing...
          </div>
        )
      case 'security-review':
        return (
          <div className="flex items-center gap-2 text-purple-700 bg-purple-50 px-3 py-1 rounded-full text-sm font-medium">
            <Shield className="h-4 w-4" />
            Pending Review
          </div>
        )
    }
  }

  const getRowBackgroundColor = (status: VendorAssessment['status']) => {
    switch (status) {
      case 'auto-approved':
      case 'exception-approved':
        return 'bg-green-50'
      case 'manager-approval':
        return 'bg-yellow-50'
      case 'exception-required':
        return 'bg-orange-50'
      case 'exception-rejected':
        return 'bg-red-50'
      case 'analyzing':
        return 'bg-blue-50'
      case 'security-review':
        return 'bg-purple-50'
      default:
        return 'bg-white'
    }
  }

  const getDataClassificationBadge = (level: string) => {
    const configs = {
      L0: { label: 'L0 - Customer Data', color: 'red' },
      L1: { label: 'L1 - PII', color: 'orange' },
      L2: { label: 'L2 - Non-public', color: 'yellow' },
      L3: { label: 'L3 - Public', color: 'green' }
    }
    const config = configs[level as keyof typeof configs]

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        {config.label}
      </span>
    )
  }

  // Filter vendors based on current view
  const filteredVendors = useMemo(() => {
    const filtered = vendors.filter(vendor => {
      if (viewFilter === 'active') {
        const result = !vendor.archived && !vendor.deleted && vendor.status !== 'exception-rejected'
        if (!result) {
          console.log('[Filter] Excluding vendor from active:', { name: vendor.vendorName, status: vendor.status, archived: vendor.archived, deleted: vendor.deleted })
        }
        return result
      } else if (viewFilter === 'archived') {
        return vendor.archived && !vendor.deleted
      } else if (viewFilter === 'deleted') {
        return vendor.deleted
      }
      return true
    })
    console.log(`[Filter] ${viewFilter} view: ${filtered.length} vendors after filtering`)

    // Apply sorting for rows view
    if (viewMode === 'rows') {
      const sorted = [...filtered].sort((a, b) => {
        let compareValue = 0

        switch (sortColumn) {
          case 'vendor':
            compareValue = a.vendorName.localeCompare(b.vendorName)
            break
          case 'status':
            compareValue = a.status.localeCompare(b.status)
            break
          case 'classification':
            compareValue = a.dataClassification.localeCompare(b.dataClassification)
            break
          case 'date':
            compareValue = new Date(a.submittedAt).getTime() - new Date(b.submittedAt).getTime()
            break
        }

        return sortDirection === 'asc' ? compareValue : -compareValue
      })
      return sorted
    }

    return filtered
  }, [vendors, viewFilter, viewMode, sortColumn, sortDirection])


  // Count vendors in each category
  const activeCount = vendors.filter(v => !v.archived && !v.deleted && v.status !== 'exception-rejected').length
  const archivedCount = vendors.filter(v => v.archived && !v.deleted).length
  const deletedCount = vendors.filter(v => v.deleted).length

  // Group vendors by review year from submittedAt timestamp
  const vendorsByYear = useMemo(() => {
    const yearMap: { [year: string]: VendorAssessment[] } = {}

    vendors.filter(v => !v.archived && !v.deleted).forEach(vendor => {
      const year = new Date(vendor.submittedAt).getFullYear().toString()
      if (!yearMap[year]) {
        yearMap[year] = []
      }
      yearMap[year].push(vendor)
    })

    return yearMap
  }, [vendors])

  // Get available years sorted descending (most recent first)
  const reviewYears = useMemo(() => {
    return Object.keys(vendorsByYear).sort((a, b) => parseInt(b) - parseInt(a))
  }, [vendorsByYear])

  // Match subprocessors with assessed vendors and separate into reviewed/pending
  const { reviewedSubprocessors, pendingSubprocessors } = useMemo(() => {
    const normalizeStr = (str: string) => str.toLowerCase().replace(/[^a-z0-9]/g, '')

    const reviewed: any[] = []
    const pending: any[] = []

    subprocessors.forEach(sub => {
      const subNameNorm = normalizeStr(sub.name)
      const subUrlNorm = normalizeStr(extractFQDN(sub.dpaUrl || sub.privacyUrl || ''))

      // Find matching vendor by normalized name or URL
      const matchingVendor = vendors.find(v => {
        if (v.archived || v.deleted) return false
        const vendorNameNorm = normalizeStr(v.vendorName)
        const vendorUrlNorm = normalizeStr(v.websiteUrl)
        return vendorNameNorm === subNameNorm || vendorUrlNorm === subUrlNorm
      })

      if (matchingVendor) {
        reviewed.push({ ...sub, vendorAssessment: matchingVendor })
      } else {
        pending.push(sub)
      }
    })

    return { reviewedSubprocessors: reviewed, pendingSubprocessors: pending }
  }, [subprocessors, vendors])

  // Group reviewed subprocessors by year
  const reviewedSubprocessorsByYear = useMemo(() => {
    const yearMap: { [year: string]: any[] } = {}

    reviewedSubprocessors.forEach(sub => {
      const year = new Date(sub.vendorAssessment.submittedAt).getFullYear().toString()
      if (!yearMap[year]) {
        yearMap[year] = []
      }
      yearMap[year].push(sub)
    })

    return yearMap
  }, [reviewedSubprocessors])

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TPRM Assessment Hero - Only show when on assessments tab */}
      {activeTab === 'assessments' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900">TPRM Assessment</h2>
          <p className="text-sm text-gray-600 mt-1">
            AI-powered vendor security evaluation against your data classification policy
          </p>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {!initialTab && (
          <div className="border-b border-gray-200">
          {/* Mobile: Hamburger + Current Tab */}
          <div className="md:hidden flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-2">
              {(() => {
                const tabs = [
                  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                  { key: 'assessments', label: 'Assessments', icon: Shield },
                  { key: 'settings', label: 'Configuration', icon: Settings }
                ]
                const currentTab = tabs.find(t => t.key === activeTab)
                const Icon = currentTab?.icon || BarChart3
                return (
                  <>
                    <Icon className="h-5 w-5 text-primary-600" />
                    <span className="font-medium text-gray-900">{currentTab?.label}</span>
                  </>
                )
              })()}
            </div>
            <button
              onClick={() => setSubMenuOpen(!subMenuOpen)}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Toggle sub menu"
            >
              {subMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Mobile: Dropdown Menu */}
          {subMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-2">
              {[
                { key: 'assessments', label: 'Assessments', icon: Shield },
                { key: 'settings', label: 'Configuration', icon: Settings }
              ].map((tab) => {
                const IconComponent = tab.icon
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key as any)
                      setSubMenuOpen(false)
                      window.location.hash = `tprm-${tab.key}`
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm font-medium ${
                      activeTab === tab.key
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <IconComponent className="h-4 w-4 mr-3" />
                    {tab.label}
                  </button>
                )
              })}
            </div>
          )}

          {/* Desktop: Horizontal Tabs */}
          <nav className="hidden md:flex space-x-8 px-6">
            {[
              { key: 'assessments', label: 'Assessments', icon: Shield },
              { key: 'settings', label: 'Configuration', icon: Settings }
            ].map((tab) => {
              const IconComponent = tab.icon
              return (
                <button
                  key={tab.key}
                  data-tour-subtab={`tprm-${tab.key}`}
                  onClick={() => {
                    setActiveTab(tab.key as any)
                    window.location.hash = `tprm-${tab.key}`
                  }}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>
        )}

        <div className="p-6">
          {activeTab === 'assessments' && (
            <div className="space-y-6" data-tour="tprm-assessments">
              {/* View Filter Tabs and View Mode Toggle */}
              {!showForm && (
                <div className="flex items-center justify-between border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setViewFilter('active')}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        viewFilter === 'active'
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Active ({activeCount})
                    </button>
                  <button
                    onClick={() => setViewFilter('archived')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      viewFilter === 'archived'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Archive className="h-4 w-4 inline mr-1" />
                    Archived ({archivedCount})
                  </button>
                  <button
                    onClick={() => setViewFilter('deleted')}
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                      viewFilter === 'deleted'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Trash2 className="h-4 w-4 inline mr-1" />
                    Recycle Bin ({deletedCount})
                  </button>
                  </div>
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-1 px-2">
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'list'
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title="Detailed list view"
                    >
                      <List className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('rows')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'rows'
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title="Condensed row view"
                    >
                      <AlignJustify className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                      title="Grid view with drag & drop"
                    >
                      <Grid3x3 className="h-4 w-4" />
                    </button>

                    {/* Separator */}
                    {!showForm && (
                      <>
                        <div className="h-6 w-px bg-gray-300 mx-2" />

                        {/* Export Button with Dropdown */}
                        <div className="relative" ref={exportMenuRef}>
                          <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            className="p-2 rounded transition-colors text-gray-500 hover:bg-gray-100"
                            title="Export assessments"
                          >
                            <Download className="h-4 w-4" />
                          </button>

                          {showExportMenu && (
                            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-10 min-w-[180px]">
                              <button
                                onClick={() => {
                                  const exportUrl = `/api/vendor-assessment-export?filter=${viewFilter}&format=json`
                                  window.open(exportUrl, '_blank')
                                  setShowExportMenu(false)
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Export JSON
                              </button>
                              <button
                                onClick={() => {
                                  const downloadUrl = `/api/vendor-assessment-download-all?filter=${viewFilter}`
                                  window.open(downloadUrl, '_blank')
                                  setShowExportMenu(false)
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Download All (ZIP)
                              </button>
                            </div>
                          )}
                        </div>

                        {/* CSV Upload Button */}
                        <button
                          onClick={() => setShowCsvUpload(true)}
                          className="p-2 rounded transition-colors text-gray-500 hover:bg-gray-100"
                          title="Upload CSV of vendors"
                        >
                          <Upload className="h-4 w-4" />
                        </button>

                        {/* Start Assessment Button */}
                        <button
                          onClick={() => setShowForm(true)}
                          className="p-2 rounded transition-colors bg-primary-600 text-white hover:bg-primary-700"
                          title="Start new assessment"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </>
                    )}

                    {/* Bulk Action Buttons */}
                    {selectedVendorIds.size > 0 && (
                      <>
                        <div className="h-6 w-px bg-gray-300 mx-2" />
                        <span className="text-xs font-medium text-gray-600 px-2">
                          {selectedVendorIds.size} selected
                        </span>
                        {viewFilter === 'active' && (
                          <>
                            <button
                              onClick={() => setShowGenerateModal(true)}
                              className="p-2 rounded transition-colors text-purple-700 hover:bg-purple-100"
                              title={`Generate AI content for ${selectedVendorIds.size} vendor${selectedVendorIds.size > 1 ? 's' : ''}`}
                            >
                              <Sparkles className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleBulkArchive}
                              className="p-2 rounded transition-colors text-blue-700 hover:bg-blue-100"
                              title={`Archive ${selectedVendorIds.size} vendor${selectedVendorIds.size > 1 ? 's' : ''}`}
                            >
                              <Archive className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleBulkMoveToRecycleBin}
                              className="p-2 rounded transition-colors text-red-700 hover:bg-red-100"
                              title={`Move ${selectedVendorIds.size} vendor${selectedVendorIds.size > 1 ? 's' : ''} to recycle bin`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}


      {/* Submission Form - Multi-Step Questionnaire - NOW IN MODAL */}
      {false && showForm && !editingVendor && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                New Vendor Assessment
              </h3>
              <p className="text-xs text-gray-500 mt-1">Step {formStep} of {formStep === 5 ? 5 : 4}</p>
            </div>
            <button
              onClick={() => {
                setShowForm(false)
                setFormStep(1)
                setEditingVendor(null)
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-600 transition-all duration-300"
                style={{ width: `${(formStep / (formStep === 5 ? 5 : 4)) * 100}%` }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 0: Website URL Only */}
            {formStep === 0 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">Quick Start</h3>
                  <p className="text-sm text-blue-700">
                    Enter the vendor's website URL and we'll automatically discover their information using AI
                    {hasApiKey === false && <span className="text-orange-600 font-medium"> (Note: You'll need to set up an Anthropic API key in Settings to use auto-discovery)</span>}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Vendor Website URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    onBlur={(e) => {
                      const cleanedUrl = extractFQDN(e.target.value)
                      if (cleanedUrl !== e.target.value) {
                        setFormData({ ...formData, websiteUrl: cleanedUrl })
                      }
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()

                        // Normalize URL: add https:// if missing protocol
                        let url = e.currentTarget.value.trim()
                        if (url && !url.match(/^https?:\/\//i)) {
                          url = 'https://' + url
                          setFormData({ ...formData, websiteUrl: url })
                        }

                        // Wait a tick for state to update, then trigger Next button logic
                        setTimeout(async () => {
                          if (!url) {
                            alert('Please enter a website URL')
                            return
                          }

                          // If API key exists, auto-trigger discovery
                          if (hasApiKey) {
                            setDiscovering(true)
                            try {
                              await handleDiscoverVendor(url)
                              setFormStep(1)
                            } catch (error) {
                              console.error('[Step 0] Auto-discovery failed:', error)
                              setFormStep(1)
                            } finally {
                              setDiscovering(false)
                            }
                          } else {
                            // No API key, just advance to manual entry
                            setFormStep(1)
                          }
                        }, 50)
                      }
                    }}
                    placeholder="https://www.vendor.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll extract the vendor name and other details from their website
                  </p>
                </div>
              </div>
            )}

            {/* Step 1: Basic Information */}
            {formStep === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Vendor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vendorName}
                    onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                    placeholder="e.g., Acme Security Inc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Vendor Website URL *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      required
                      value={formData.websiteUrl}
                      onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                      onBlur={(e) => {
                        const cleanedUrl = extractFQDN(e.target.value)
                        if (cleanedUrl !== e.target.value) {
                          setFormData({ ...formData, websiteUrl: cleanedUrl })
                        }
                      }}
                      placeholder="https://www.vendor.com"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={handleDiscoverVendor}
                      disabled={discovering || !formData.vendorName || !formData.websiteUrl}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      title="Use AI to analyze vendor website and generate purpose and description"
                    >
                      {discovering ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Query Vendor
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Click "Query Vendor" to auto-populate purpose and description fields
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose (3 words max)
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.purpose || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      const wordCount = value.trim().split(/\s+/).filter(w => w.length > 0).length
                      if (wordCount <= 3 || value === '') {
                        setFormData({
                          ...formData,
                          purpose: value,
                          questionnaire: {
                            ...formData.questionnaire,
                            servicePurpose: value
                          }
                        })
                      }
                    }}
                    placeholder="e.g., Password management tool"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A short 3-word summary of the vendor's primary function
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.aiSummary || ''}
                    onChange={(e) => setFormData({ ...formData, aiSummary: e.target.value })}
                    placeholder="e.g., Cloud-based password management and credential storage service..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A concise description of the vendor's service offering and security posture (2-3 sentences)
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Value (Annual)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.contractValue || ''}
                        onChange={(e) => setFormData({ ...formData, contractValue: e.target.value ? parseFloat(e.target.value) : undefined })}
                        placeholder="e.g., 12000"
                        min="0"
                        step="0.01"
                        className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Annual contract value in USD (optional)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Licenses
                    </label>
                    <input
                      type="number"
                      value={formData.numberOfLicenses || ''}
                      onChange={(e) => setFormData({ ...formData, numberOfLicenses: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="e.g., 50"
                      min="1"
                      step="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of licenses/seats purchased (optional)
                    </p>
                  </div>
                </div>

                {/* Subprocessor Checkbox */}
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="isSubprocessor"
                    checked={formData.isSubprocessor || false}
                    onChange={(e) => setFormData({ ...formData, isSubprocessor: e.target.checked })}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="isSubprocessor" className="block text-sm font-medium text-gray-900 cursor-pointer">
                      Mark as Subprocessor
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      Check this if the vendor will process data on your behalf (automatically checked if DPA is provided)
                    </p>
                  </div>
                </div>

                {/* AI Suggested Classification */}
                {suggestedClassification && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-purple-900 mb-1">
                          AI-Suggested Data Classification
                        </h4>
                        <div className="mb-2">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            suggestedClassification.level === 'L0' ? 'bg-red-100 text-red-800' :
                            suggestedClassification.level === 'L1' ? 'bg-orange-100 text-orange-800' :
                            suggestedClassification.level === 'L2' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {suggestedClassification.level} - {
                              suggestedClassification.level === 'L0' ? 'Customer Data' :
                              suggestedClassification.level === 'L1' ? 'PII' :
                              suggestedClassification.level === 'L2' ? 'Non-public' :
                              'Public'
                            }
                          </span>
                        </div>
                        <p className="text-sm text-purple-800 mb-3">
                          <span className="font-medium">Reasoning:</span> {suggestedClassification.reasoning}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-purple-700">
                            This is a suggestion based on AI analysis. You'll answer questions in the next steps to confirm the classification.
                          </p>
                          <button
                            type="button"
                            onClick={() => setSuggestedClassification(null)}
                            className="text-xs text-purple-600 hover:text-purple-800 underline ml-auto"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Upload className="h-4 w-4 inline mr-2" />
                    Upload Questionnaire (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Upload a completed vendor questionnaire document (PDF, Word, Excel, or text file, max 10MB)
                  </p>

                  {!uploadedFile ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                      <input
                        type="file"
                        id="questionnaire-upload"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="questionnaire-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Click to upload or drag and drop
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          PDF, Word, Excel, or Text (max 10MB)
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileIcon className="h-5 w-5 text-primary-600" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{uploadedFile.name}</div>
                            <div className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeUploadedFile}
                          className="p-1 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-50"
                          title="Remove file"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    This file will be preserved for reference and can be viewed after submission.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Privacy Policy Analysis */}
            {formStep === 2 && (
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Privacy Policy Analysis</h4>
                  <p className="text-sm text-purple-800">
                    We'll automatically scan the vendor's privacy policy to determine data access patterns.
                  </p>
                </div>

                {analyzingPrivacyPolicy && (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin text-4xl mb-4">⏳</div>
                      <p className="text-lg font-medium text-gray-900">{privacyPolicyAnalysisStatus}</p>
                      <p className="text-sm text-gray-600 mt-2">
                        Scanning {formData.websiteUrl} for privacy policy, DPA, and analyzing data access patterns
                      </p>
                    </div>
                  </div>
                )}

                {!analyzingPrivacyPolicy && privacyPolicyAnalysis && (
                  <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h5 className="text-md font-semibold text-gray-900 mb-4">Analysis Results</h5>

                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Privacy Policy URL:</label>
                          <p className="mt-1">
                            <a
                              href={privacyPolicyAnalysis.privacyPolicyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              {privacyPolicyAnalysis.privacyPolicyUrl}
                            </a>
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">DPA (Data Processing Agreement) URL:</label>
                          <p className="mt-1">
                            {privacyPolicyAnalysis.dpaUrl ? (
                              <a
                                href={privacyPolicyAnalysis.dpaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                              >
                                {privacyPolicyAnalysis.dpaUrl}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-500 italic">Not found on vendor website</span>
                            )}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="border rounded-lg p-4">
                            <label className="text-sm font-medium text-gray-700">Customer Data Access:</label>
                            <div className="mt-2">
                              {privacyPolicyAnalysis.accessCustomerData ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                  YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  NO
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="border rounded-lg p-4">
                            <label className="text-sm font-medium text-gray-700">Employee PII Access:</label>
                            <div className="mt-2">
                              {privacyPolicyAnalysis.accessEmployeePII ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                                  YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  NO
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {privacyPolicyAnalysis.dataTypes && privacyPolicyAnalysis.dataTypes.length > 0 && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Data Types Collected:</label>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {privacyPolicyAnalysis.dataTypes.map((type, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="text-sm font-medium text-gray-700">AI Reasoning:</label>
                          <p className="mt-1 text-sm text-gray-600">
                            {privacyPolicyAnalysis.analysisReason}
                          </p>
                        </div>

                        {privacyPolicyAnalysis.accessCustomerData && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <p className="text-sm text-orange-900">
                              ⚠️ This vendor has been automatically marked as a subprocessor and will require a Data Processing Agreement (DPA).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <input
                        type="text"
                        placeholder="Adjust privacy policy URL if needed"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        defaultValue={privacyPolicyAnalysis.privacyPolicyUrl}
                      />
                      <button
                        type="button"
                        onClick={handleAnalyzePrivacyPolicy}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                      >
                        Re-scan
                      </button>
                    </div>
                  </div>
                )}

                {!analyzingPrivacyPolicy && !privacyPolicyAnalysis && (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                    <div className="text-center mb-6">
                      <p className="text-sm font-medium text-gray-900 mb-2">
                        Could not automatically locate privacy policy
                      </p>
                      <p className="text-xs text-gray-600 mb-4">
                        Please manually indicate data access patterns below, or adjust the URL and retry the scan
                      </p>
                    </div>

                    {/* Manual Override Checkboxes */}
                    <div className="space-y-4 mb-6">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.questionnaire?.accessCustomerData === true}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                questionnaire: {
                                  ...formData.questionnaire,
                                  accessCustomerData: e.target.checked
                                }
                              })
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              This vendor has access to customer data
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Check if vendor processes customer contact info, account data, usage data, payment info, or any end-user data
                            </p>
                          </div>
                        </label>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.questionnaire?.accessEmployeePII === true}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                questionnaire: {
                                  ...formData.questionnaire,
                                  accessEmployeePII: e.target.checked
                                }
                              })
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              This vendor has access to employee PII
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Check if vendor processes employee names, emails, HR data, credentials, or workspace/collaboration data
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Retry Analysis Section */}
                    <div className="border-t border-yellow-200 pt-4">
                      <div className="flex items-center gap-4">
                        <input
                          type="text"
                          placeholder="Adjust privacy policy URL if needed"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          defaultValue={formData.websiteUrl}
                        />
                        <button
                          type="button"
                          onClick={handleAnalyzePrivacyPolicy}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm whitespace-nowrap"
                        >
                          Retry Scan
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Additional Context */}
            {formStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Context (Optional)
                  </label>
                  <textarea
                    value={formData.questionnaire.additionalContext}
                    onChange={(e) => setFormData({
                      ...formData,
                      questionnaire: { ...formData.questionnaire, additionalContext: e.target.value }
                    })}
                    placeholder="Any additional information about how this vendor will be used or data they'll access..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Ready to Analyze</h4>
                  <p className="text-sm text-green-800">
                    Click "Analyze Vendor" to check the vendor's security posture and determine their data classification level.
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Attestation Confirmation (if needed) */}
            {formStep === 5 && (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                        Security Attestation Verification
                      </h4>
                      <p className="text-sm text-yellow-800 mb-3">
                        Based on your data classification, the following security attestations are required or recommended for this vendor.
                        <span className="font-semibold"> Attestations marked as REQUIRED must be confirmed for auto-approval.</span>
                      </p>
                      <p className="text-sm text-yellow-800">
                        Please check the vendor's website or documentation, select which attestations they have, and provide proof (upload a document OR enter a URL):
                      </p>
                    </div>
                  </div>
                </div>

                {/* Interactive Checkboxes with File Upload */}
                <div className="space-y-3">
                  {uncertainAttestations.map((attestation) => {
                    const isSelected = confirmedAttestations.includes(attestation.label)
                    const uploadedFile = attestationFiles.get(attestation.label)
                    const borderColor = attestation.required ? 'border-red-400' : 'border-gray-200'
                    const bgColor = attestation.required ? 'bg-red-50' : 'bg-white'
                    const hoverColor = attestation.required ? 'hover:bg-red-100' : 'hover:bg-gray-50'

                    return (
                      <div
                        key={attestation.id}
                        className={`p-4 rounded-lg border-2 ${
                          isSelected
                            ? attestation.required ? 'bg-red-100 border-red-500' : 'bg-blue-50 border-blue-400'
                            : `${bgColor} ${borderColor} ${hoverColor}`
                        }`}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAttestationSelection(attestation.label)}
                            className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500 mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">{attestation.label}</span>
                              {attestation.required ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-600 text-white">
                                  REQUIRED
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-500 text-white">
                                  OPTIONAL
                                </span>
                              )}
                            </div>

                            {isSelected && (
                              <div className="mt-3 space-y-3">
                                <div className="text-xs text-gray-600 font-medium">
                                  {attestation.required ? 'Provide proof (required):' : 'Provide proof (optional):'}
                                </div>

                                {/* File Upload Section */}
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-700 font-medium">Upload Document:</div>
                                  {!uploadedFile ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="file"
                                        id={`attestation-file-${attestation.id}`}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0]
                                          if (file) {
                                            // Validate file size (max 10MB)
                                            if (file.size > 10 * 1024 * 1024) {
                                              alert('File size must be less than 10MB')
                                              e.target.value = ''
                                              return
                                            }
                                            handleAttestationFileUpload(attestation.label, file)
                                          }
                                        }}
                                        className="hidden"
                                      />
                                      <label
                                        htmlFor={`attestation-file-${attestation.id}`}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                                      >
                                        <Upload className="h-3 w-3" />
                                        Choose File
                                      </label>
                                      <span className="text-xs text-gray-500">PDF, Word, or Image (max 10MB)</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-2 bg-white border border-gray-300 rounded-md">
                                      <FileIcon className="h-4 w-4 text-green-600" />
                                      <span className="text-xs text-gray-900 flex-1 truncate">{uploadedFile.name}</span>
                                      <span className="text-xs text-gray-500">
                                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleAttestationFileUpload(attestation.label, null)}
                                        className="p-1 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-50"
                                        title="Remove file"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* OR Divider */}
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 border-t border-gray-300"></div>
                                  <span className="text-xs text-gray-500 font-medium">OR</span>
                                  <div className="flex-1 border-t border-gray-300"></div>
                                </div>

                                {/* URL Input Section */}
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-700 font-medium">Enter URL:</div>
                                  {!attestationUrls.has(attestation.label) ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="url"
                                        placeholder="https://example.com/soc2-report.pdf"
                                        className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault()
                                            const input = e.target as HTMLInputElement
                                            if (input.value.trim()) {
                                              handleAttestationUrlInput(attestation.label, input.value)
                                              input.value = ''
                                            }
                                          }
                                        }}
                                        onBlur={(e) => {
                                          if (e.target.value.trim()) {
                                            handleAttestationUrlInput(attestation.label, e.target.value)
                                            e.target.value = ''
                                          }
                                        }}
                                      />
                                      <span className="text-xs text-gray-500 whitespace-nowrap">Press Enter</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-300 rounded-md">
                                      <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                      <a
                                        href={attestationUrls.get(attestation.label)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline flex-1 truncate"
                                      >
                                        {attestationUrls.get(attestation.label)}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleAttestationUrlInput(attestation.label, null)}
                                        className="p-1 text-blue-600 hover:text-red-600 rounded-md hover:bg-red-50 flex-shrink-0"
                                        title="Remove URL"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    )
                  })}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Check the vendor's website (<a href={formData.websiteUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">{formData.websiteUrl}</a>)
                    or contact them directly to verify their security certifications. You can upload proof documents OR provide URLs to where the evidence is located.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Ready to Submit</h4>
                  <p className="text-sm text-green-800">
                    Once you've confirmed the vendor's attestations and provided proof (documents or URLs), click "Submit for Analysis" to complete the assessment.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  if (formStep === 5) {
                    // Go back to step 4 from attestation step
                    setFormStep(5)
                    setUncertainAttestations([])
                    setConfirmedAttestations([])
                  } else {
                    setFormStep(Math.max(1, formStep - 1))
                  }
                }}
                disabled={formStep === 0 || analyzingVendor || isSubmitting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex gap-3">
                {formStep < 3 ? (
                  <button
                    type="button"
                    onClick={async () => {
                      // Step 0: Validate website URL and trigger auto-discovery
                      if (formStep === 0) {
                        if (!formData.websiteUrl) {
                          alert('Please enter a website URL')
                          return
                        }

                        // Normalize URL: add https:// if missing protocol
                        let normalizedUrl = formData.websiteUrl.trim()
                        if (normalizedUrl && !normalizedUrl.match(/^https?:\/\//i)) {
                          normalizedUrl = 'https://' + normalizedUrl
                        }

                        // If API key exists, auto-trigger discovery
                        if (hasApiKey) {
                          setDiscovering(true)
                          try {
                            await handleDiscoverVendor(normalizedUrl)
                            // handleDiscoverVendor already sets formData, just advance
                            setFormStep(1)
                          } catch (error) {
                            console.error('[Step 0] Auto-discovery failed:', error)
                            // Still advance to manual entry
                            setFormStep(1)
                          } finally {
                            setDiscovering(false)
                          }
                        } else {
                          // No API key, just advance to manual entry
                          setFormStep(1)
                        }
                        return
                      }

                      // Step 1: Validate basic information
                      if (formStep === 1 && (!formData.vendorName || !formData.websiteUrl || !formData.questionnaire.servicePurpose)) {
                        console.log('[Step 1 Validation] Missing fields:', {
                          vendorName: formData.vendorName,
                          websiteUrl: formData.websiteUrl,
                          servicePurpose: formData.questionnaire.servicePurpose
                        })
                        alert('Please fill in all required fields')
                        return
                      }

                      setFormStep(formStep + 1)
                    }}
                    disabled={discovering}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {discovering && formStep === 0 && <Loader2 className="h-4 w-4 animate-spin" />}
                    {discovering && formStep === 0 ? 'Discovering...' : 'Next'}
                  </button>
                ) : formStep === 3 ? (
                  <button
                    type="submit"
                    disabled={analyzingVendor}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {analyzingVendor && <Loader2 className="h-4 w-4 animate-spin" />}
                    {analyzingVendor ? 'Analyzing...' : 'Analyze Vendor'}
                  </button>
                ) : formStep === 3 ? (
                  <button
                    type="button"
                    onClick={() => submitFinalAssessment(confirmedAttestations)}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit for Analysis'}
                  </button>
                ) : formStep === 5 ? (
                  <button
                    type="button"
                    onClick={() => submitFinalAssessment(confirmedAttestations)}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                ) : null}
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Vendors List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Loader2 className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Vendor Assessments...</h3>
            <p className="text-sm text-gray-600">Please wait while we fetch your data</p>
          </div>
        ) : filteredVendors.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {viewFilter === 'active' && vendors.length === 0 ? 'No Vendors Assessed Yet' :
               viewFilter === 'archived' ? 'No Archived Vendors' :
               'Recycle Bin Empty'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {viewFilter === 'active' && vendors.length === 0
                ? 'Start by adding a vendor to evaluate their security posture'
                : viewFilter === 'archived'
                ? 'Archived vendors will appear here'
                : 'Deleted vendors will appear here'}
            </p>
            {viewFilter === 'active' && vendors.length === 0 && (
              <button
                onClick={() => setShowForm(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Add Your First Vendor
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          // Grid View with Drag & Drop
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredVendors.map((vendor) => (
              <div
                key={vendor.id}
                onDragOver={(e) => handleDragOver(e, vendor.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, vendor)}
                className={`bg-white border-2 rounded-lg p-4 transition-all ${
                  draggingOver === vendor.id
                    ? 'border-primary-500 bg-primary-50 shadow-lg scale-105'
                    : selectedVendorIds.has(vendor.id)
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300'
                } ${uploadingForVendor === vendor.id ? 'opacity-50 pointer-events-none' : ''}`}
              >
                {/* Vendor Header */}
                <div className="mb-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {/* Selection Checkbox */}
                      {viewFilter === 'active' && (
                        <input
                          type="checkbox"
                          checked={selectedVendorIds.has(vendor.id)}
                          onChange={() => toggleVendorSelection(vendor.id)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer flex-shrink-0"
                        />
                      )}
                      <h3 className="text-base font-semibold text-gray-900 truncate">{vendor.vendorName}</h3>
                    </div>
                    {viewFilter === 'active' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          handleEdit(vendor)
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
                        title="Re-assess vendor"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {getStatusBadge(vendor.status)}
                </div>

                {/* Drop Zone Indicator */}
                {(vendor.status === 'exception-required' ||
                  (vendor.uncertainAttestations && vendor.uncertainAttestations.length > 0)) && (
                  <div className={`mb-3 p-3 rounded-lg border-2 border-dashed text-center transition-all ${
                    draggingOver === vendor.id
                      ? 'border-primary-500 bg-primary-100'
                      : 'border-gray-300 bg-gray-50'
                  }`}>
                    <Upload className={`h-6 w-6 mx-auto mb-1 ${
                      draggingOver === vendor.id ? 'text-primary-600' : 'text-gray-400'
                    }`} />
                    <p className="text-xs font-medium text-gray-700">
                      {vendor.status === 'exception-required'
                        ? 'Drop exception approval'
                        : 'Drop attestations here'}
                    </p>
                    {vendor.uncertainAttestations && vendor.uncertainAttestations.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Need: {vendor.uncertainAttestations.map(a => a.label).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* Vendor Info */}
                <div className="space-y-2 text-xs">
                  <a
                    href={vendor.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-600 hover:text-primary-600 truncate"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Globe className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate">{vendor.websiteUrl}</span>
                  </a>
                  {getDataClassificationBadge(vendor.dataClassification)}

                  {/* Purpose - 3 word summary */}
                  {vendor.purpose && (
                    <div className="flex items-start gap-1 text-gray-700 mt-2">
                      <span className="font-semibold text-gray-500 text-xs">Purpose:</span>
                      <span className="text-xs italic">{vendor.purpose}</span>
                    </div>
                  )}

                  {/* AI-Generated Description */}
                  {vendor.aiSummary && (
                    <div className="text-gray-600 text-xs mt-2 line-clamp-2">
                      {vendor.aiSummary}
                    </div>
                  )}

                  {/* Cost per License */}
                  {vendor.contractValue && vendor.numberOfLicenses && (
                    <div className="flex items-start gap-1 text-gray-700 mt-2">
                      <span className="font-semibold text-gray-500 text-xs">Cost/License:</span>
                      <span className="text-xs">${(vendor.contractValue / vendor.numberOfLicenses).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {uploadingForVendor === vendor.id && (
                  <div className="mt-3 flex items-center justify-center gap-2 text-sm text-primary-600">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Uploading...</span>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {new Date(vendor.submittedAt).toLocaleDateString()}
                  </span>
                  {viewFilter === 'active' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleArchive(vendor.id, vendor.vendorName)}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Archive"
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleMoveToRecycleBin(vendor.id, vendor.vendorName)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  {viewFilter === 'archived' && (
                    <button
                      onClick={() => handleRestore(vendor.id, vendor.vendorName)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Restore"
                    >
                      <ArchiveRestore className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {viewFilter === 'deleted' && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleRestore(vendor.id, vendor.vendorName)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Restore"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(vendor.id, vendor.vendorName)}
                        disabled={deletingId === vendor.id}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Permanent Delete"
                      >
                        {deletingId === vendor.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'rows' ? (
          // Condensed Row View
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[auto_1fr] gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-700">
              {/* Select All checkbox - only in active view and rows mode */}
              {viewFilter === 'active' && (
                <div className="w-6 flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedVendorIds.size === filteredVendors.length && filteredVendors.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                    title="Select All"
                  />
                </div>
              )}
              <div className="grid grid-cols-12 gap-4">
                <button
                  onClick={() => handleSort('vendor')}
                  className="col-span-2 flex items-center gap-1 hover:text-primary-600 cursor-pointer text-left"
                >
                  Vendor
                  {sortColumn === 'vendor' && (
                    sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                  )}
                </button>
                <button
                  onClick={() => handleSort('status')}
                  className="col-span-3 flex items-center gap-1 hover:text-primary-600 cursor-pointer text-left"
                >
                  Status
                  {sortColumn === 'status' && (
                    sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                  )}
                </button>
                <button
                  onClick={() => handleSort('classification')}
                  className="col-span-2 flex items-center gap-1 hover:text-primary-600 cursor-pointer text-left"
                >
                  Class
                  {sortColumn === 'classification' && (
                    sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                  )}
                </button>
                <div className="col-span-1 text-center text-xs">Sub</div>
                <button
                  onClick={() => handleSort('date')}
                  className="col-span-1 flex items-center gap-1 hover:text-primary-600 cursor-pointer text-left"
                >
                  Date
                  {sortColumn === 'date' && (
                    sortDirection === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                  )}
                </button>
                <div className="col-span-1 text-center text-xs">Cost/Lic</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
            </div>
            {/* Table Rows */}
            <div className="divide-y divide-gray-200">
              {filteredVendors.map((vendor) => (
                <div
                  key={vendor.id}
                  onDragOver={(e) => handleDragOver(e, vendor.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, vendor)}
                  className={`grid grid-cols-[auto_1fr] gap-4 px-4 py-3 items-center transition-colors ${
                    draggingOver === vendor.id
                      ? 'bg-primary-50 border-l-4 border-l-primary-500'
                      : selectedVendorIds.has(vendor.id)
                      ? 'bg-primary-50 border-l-4 border-l-primary-500'
                      : getRowBackgroundColor(vendor.status)
                  } ${uploadingForVendor === vendor.id ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {/* Selection Checkbox */}
                  {viewFilter === 'active' && (
                    <div className="w-6 flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedVendorIds.has(vendor.id)}
                        onChange={() => toggleVendorSelection(vendor.id)}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Vendor Name & Website */}
                  <div className="col-span-2">
                    <div className="font-medium text-sm text-gray-900 truncate">{vendor.vendorName}</div>
                    <a
                      href={vendor.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-gray-500 hover:text-primary-600 truncate block"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {vendor.websiteUrl}
                    </a>

                    {/* Purpose - 3 word summary */}
                    {vendor.purpose && (
                      <div className="text-xs text-gray-600 mt-0.5">
                        <span className="font-semibold">Purpose:</span> <span className="italic">{vendor.purpose}</span>
                      </div>
                    )}

                    {/* AI-Generated Description */}
                    {vendor.aiSummary && (
                      <div className="text-xs text-gray-600 mt-0.5 line-clamp-1">
                        {vendor.aiSummary}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div className="col-span-3">
                    {getStatusBadge(vendor.status)}
                  </div>

                  {/* Classification */}
                  <div className="col-span-2">
                    {getDataClassificationBadge(vendor.dataClassification)}
                  </div>

                  {/* Subprocessor Toggle */}
                  <div className="col-span-1 flex justify-center">
                    <input
                      type="checkbox"
                      checked={vendor.isSubprocessor || false}
                      onChange={() => handleToggleSubprocessor(vendor.id, vendor.isSubprocessor || false)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                      title={vendor.isSubprocessor ? "This is a subprocessor" : "Mark as subprocessor"}
                    />
                  </div>

                  {/* Date */}
                  <div className="col-span-1 text-sm text-gray-600">
                    {new Date(vendor.submittedAt).toLocaleDateString()}
                  </div>

                  {/* Cost per License */}
                  <div className="col-span-1 text-center text-sm text-gray-600">
                    {vendor.contractValue && vendor.numberOfLicenses
                      ? `$${(vendor.contractValue / vendor.numberOfLicenses).toFixed(2)}`
                      : '-'
                    }
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    {viewFilter === 'active' && (
                      <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            setViewingVendor(vendor)
                          }}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault()
                            handleEdit(vendor)
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Re-assess"
                        >
                          <Edit2 className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleArchive(vendor.id, vendor.vendorName)}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Archive"
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handleMoveToRecycleBin(vendor.id, vendor.vendorName)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    {viewFilter === 'archived' && (
                      <button
                        onClick={() => handleRestore(vendor.id, vendor.vendorName)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Restore"
                      >
                        <ArchiveRestore className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {viewFilter === 'deleted' && (
                      <>
                        <button
                          onClick={() => handleRestore(vendor.id, vendor.vendorName)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Restore"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(vendor.id, vendor.vendorName)}
                          disabled={deletingId === vendor.id}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Permanent Delete"
                        >
                          {deletingId === vendor.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // List View (existing)
          filteredVendors.map((vendor) => (
            <React.Fragment key={vendor.id}>
            <div className={`bg-white border rounded-lg p-6 ${
              selectedVendorIds.has(vendor.id)
                ? 'border-primary-500 ring-2 ring-primary-200'
                : 'border-gray-200'
            }`}>
              <div className="flex gap-4">
                {/* Selection Checkbox */}
                {viewFilter === 'active' && (
                  <div className="flex items-start pt-1">
                    <input
                      type="checkbox"
                      checked={selectedVendorIds.has(vendor.id)}
                      onChange={() => toggleVendorSelection(vendor.id)}
                      className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                    />
                  </div>
                )}

                {/* Vendor Content */}
                <div className="flex-1">
                  {/* Vendor Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{vendor.vendorName}</h3>
                        {getStatusBadge(vendor.status)}
                      </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <a
                      href={vendor.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 hover:text-primary-600"
                    >
                      <Globe className="h-4 w-4" />
                      {vendor.websiteUrl}
                    </a>
                    {getDataClassificationBadge(vendor.dataClassification)}
                  </div>

                  {/* Purpose - 3 word summary */}
                  {vendor.purpose && (
                    <div className="flex items-start gap-1 text-gray-700 mt-2">
                      <span className="font-semibold text-gray-500 text-sm">Purpose:</span>
                      <span className="text-sm italic">{vendor.purpose}</span>
                    </div>
                  )}

                  {/* AI-Generated Description */}
                  {vendor.aiSummary && (
                    <div className="text-gray-600 text-sm mt-2 line-clamp-2">
                      {vendor.aiSummary}
                    </div>
                  )}

                  {/* Cost per License */}
                  {vendor.contractValue && vendor.numberOfLicenses && (
                    <div className="flex items-start gap-1 text-gray-700 mt-2">
                      <span className="font-semibold text-gray-500 text-sm">Cost/License:</span>
                      <span className="text-sm">${(vendor.contractValue / vendor.numberOfLicenses).toFixed(2)}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {viewFilter === 'active' && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          handleEdit(vendor)
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Re-assess vendor"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleArchive(vendor.id, vendor.vendorName)}
                        className="p-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                        title="Archive assessment"
                      >
                        <Archive className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMoveToRecycleBin(vendor.id, vendor.vendorName)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Move to recycle bin"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {viewFilter === 'archived' && (
                    <>
                      <button
                        onClick={() => handleUnarchive(vendor.id, vendor.vendorName)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                        title="Restore to active"
                      >
                        <ArchiveRestore className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMoveToRecycleBin(vendor.id, vendor.vendorName)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Move to recycle bin"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {viewFilter === 'deleted' && (
                    <>
                      <button
                        onClick={() => handleRestore(vendor.id, vendor.vendorName)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                        title="Restore assessment"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(vendor.id, vendor.vendorName)}
                        disabled={deletingId === vendor.id}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
                        title="Permanently delete"
                      >
                        {deletingId === vendor.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Auto-Approved Banner */}
              {vendor.status === 'auto-approved' && (
                <div className="mb-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-600 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-green-900 mb-1">
                        ✓ Vendor Approved - Ready to Use
                      </h4>
                      <p className="text-sm text-green-800">
                        This vendor has been automatically approved based on their security posture and data classification.
                        You can proceed with using this vendor for your business needs.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Exception Required Banner */}
              {vendor.status === 'exception-required' && (
                <div className="mb-4 bg-gradient-to-r from-orange-50 to-orange-100 border-2 border-orange-600 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-orange-900 mb-1">
                        ⚠ Exception Required
                      </h4>
                      <p className="text-sm text-orange-800 mb-3">
                        This vendor is missing required security artifacts. To proceed, you must get approval from your manager
                        and upload a screenshot of the approval (email, Slack message, etc.).
                      </p>
                      {!vendor.exceptionRequest && (
                        <button
                          onClick={() => {
                            setExceptionVendor(vendor)
                            setExceptionReason('')
                            setExceptionScreenshot(null)
                            setShowExceptionModal(true)
                          }}
                          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
                        >
                          Submit Exception Request
                        </button>
                      )}
                      {vendor.exceptionRequest && !vendor.exceptionReview && (
                        <div className="bg-white border-2 border-orange-300 rounded-lg p-4 mt-4">
                          <div className="flex items-center gap-2 text-orange-900 mb-3">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="font-semibold text-sm">Exception Request Pending Review</span>
                          </div>

                          {/* Request Details */}
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs mb-3">
                              <div>
                                <p className="text-gray-500">Requested By</p>
                                <p className="font-medium text-gray-900">{vendor.exceptionRequest.requestedBy}</p>
                              </div>
                              <div>
                                <p className="text-gray-500">Requested On</p>
                                <p className="font-medium text-gray-900">
                                  {new Date(vendor.exceptionRequest.requestedAt).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="mb-3">
                              <p className="text-gray-500 text-xs mb-1">Reason</p>
                              <p className="text-sm text-gray-900 bg-white rounded p-2 border border-gray-200">
                                {vendor.exceptionRequest.reason}
                              </p>
                            </div>
                            {vendor.exceptionRequest.approvalScreenshot && (
                              <div>
                                <p className="text-gray-500 text-xs mb-1">Manager Approval Screenshot</p>
                                <a
                                  href={`/api/vendor-exception-request?fileKey=${vendor.exceptionRequest.approvalScreenshot.fileKey}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 bg-blue-50 px-3 py-1.5 rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View Screenshot: {vendor.exceptionRequest.approvalScreenshot.filename}
                                </a>
                              </div>
                            )}
                          </div>

                          {/* Security Team Review Form */}
                          {reviewingVendorId === vendor.id ? (
                            <div className="border-t border-orange-200 pt-3">
                              <p className="text-sm font-medium text-gray-900 mb-2">Security Team Review</p>
                              <textarea
                                value={reviewComments}
                                onChange={(e) => setReviewComments(e.target.value)}
                                placeholder="Add comments about this exception decision (required for rejections)..."
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent mb-3"
                                disabled={submittingReview}
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleReviewException(vendor.id, 'approved')}
                                  disabled={submittingReview}
                                  className="flex-1 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                                >
                                  {submittingReview && <Loader2 className="h-4 w-4 animate-spin" />}
                                  <CheckCircle className="h-4 w-4" />
                                  Approve Exception
                                </button>
                                <button
                                  onClick={() => handleReviewException(vendor.id, 'rejected')}
                                  disabled={submittingReview}
                                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                                >
                                  {submittingReview && <Loader2 className="h-4 w-4 animate-spin" />}
                                  <XCircle className="h-4 w-4" />
                                  Reject Exception
                                </button>
                                <button
                                  onClick={() => {
                                    setReviewingVendorId(null)
                                    setReviewComments('')
                                  }}
                                  disabled={submittingReview}
                                  className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setReviewingVendorId(vendor.id)}
                              className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Review Exception Request
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Exception Approved Banner */}
              {vendor.status === 'exception-approved' && (
                <div className="mb-4 bg-gradient-to-r from-green-50 to-green-100 border-2 border-green-600 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-green-900 mb-1">
                        ✓ Exception Approved - Ready to Use
                      </h4>
                      <p className="text-sm text-green-800 mb-2">
                        Manager approval received. This vendor has been approved with an exception for missing artifacts.
                        You can proceed with using this vendor for your business needs.
                      </p>
                      {vendor.exceptionReview && (
                        <div className="bg-white border border-green-300 rounded-lg p-2 text-xs text-gray-700">
                          <p><strong>Reviewed by:</strong> {vendor.exceptionReview.reviewedBy}</p>
                          <p><strong>Date:</strong> {new Date(vendor.exceptionReview.reviewedAt).toLocaleDateString()}</p>
                          {vendor.exceptionReview.comments && (
                            <p><strong>Comments:</strong> {vendor.exceptionReview.comments}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Exception Rejected Banner */}
              {vendor.status === 'exception-rejected' && (
                <div className="mb-4 bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-600 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                        <XCircle className="h-6 w-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-bold text-red-900 mb-1">
                        ✗ Exception Rejected
                      </h4>
                      <p className="text-sm text-red-800 mb-2">
                        The exception request for this vendor has been rejected. This vendor cannot be used until
                        the required security artifacts are provided or a new exception is approved.
                      </p>
                      {vendor.exceptionReview && (
                        <div className="bg-white border border-red-300 rounded-lg p-2 text-xs text-gray-700">
                          <p><strong>Reviewed by:</strong> {vendor.exceptionReview.reviewedBy}</p>
                          <p><strong>Date:</strong> {new Date(vendor.exceptionReview.reviewedAt).toLocaleDateString()}</p>
                          {vendor.exceptionReview.comments && (
                            <p><strong>Rejection reason:</strong> {vendor.exceptionReview.comments}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Service Purpose / Use Case */}
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">
                    {vendor.questionnaire ? 'Service Purpose:' : 'Use Case:'}
                  </span>{' '}
                  {vendor.questionnaire?.servicePurpose || vendor.useCase || 'N/A'}
                </p>
              </div>

              {/* Uploaded Questionnaire File */}
              {vendor.uploadedFile && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Uploaded Questionnaire</h4>
                  <a
                    href={vendor.uploadedFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors text-sm"
                  >
                    <FileIcon className="h-4 w-4 text-primary-600" />
                    <span className="text-gray-900 font-medium">{vendor.uploadedFile.filename}</span>
                    <span className="text-gray-500 text-xs">
                      (Uploaded {new Date(vendor.uploadedFile.uploadedAt).toLocaleDateString()})
                    </span>
                  </a>
                </div>
              )}

              {/* Data Classification Reasoning */}
              {vendor.classificationReasoning && vendor.status !== 'analyzing' && (
                <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-900">
                    <span className="font-semibold">Classification Reasoning:</span> {vendor.classificationReasoning}
                  </p>
                </div>
              )}

              {/* Security Posture */}
              {vendor.status !== 'analyzing' && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Security Posture</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-2">
                    {[
                      { label: 'Privacy Policy', value: vendor.securityPosture.hasPrivacyPolicy },
                      { label: 'SOC 2', value: vendor.securityPosture.hasSoc2 },
                      { label: 'ISO 27001', value: vendor.securityPosture.hasIso27001 },
                      { label: 'Penetration Testing', value: vendor.securityPosture.hasPenetrationTesting },
                      { label: 'DPA', value: vendor.securityPosture.hasDpa }
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`text-xs px-2 py-1 rounded ${
                          item.value
                            ? 'bg-green-50 text-green-700'
                            : 'bg-gray-50 text-gray-500'
                        }`}
                      >
                        {item.value ? '✓' : '✗'} {item.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {vendor.status !== 'analyzing' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">AI Analysis Summary</h4>
                  <p className="text-sm text-blue-800">{vendor.aiSummary}</p>
                </div>
              )}

              {/* Metadata */}
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                Submitted by {vendor.submittedBy} on {new Date(vendor.submittedAt).toLocaleDateString()}
              </div>
                </div>
              </div>
            </div>

            {/* Edit Form - NOW IN MODAL */}
            {false && showForm && editingVendor?.id === vendor.id && (
              <div className="mt-4 bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Re-assess Vendor
                    </h3>
                    <p className="text-xs text-yellow-600 mt-1">
                      Creating new assessment for {editingVendor.vendorName}. Previous assessment will be preserved.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Step {formStep} of {formStep === 5 ? 5 : 4}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForm(false)
                      setFormStep(1)
                      setEditingVendor(null)
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 transition-all duration-300"
                      style={{ width: `${(formStep / (formStep === 5 ? 5 : 4)) * 100}%` }}
                    />
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 0: Website URL Only */}
            {formStep === 0 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">Quick Start</h3>
                  <p className="text-sm text-blue-700">
                    Enter the vendor's website URL and we'll automatically discover their information using AI
                    {hasApiKey === false && <span className="text-orange-600 font-medium"> (Note: You'll need to set up an Anthropic API key in Settings to use auto-discovery)</span>}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Vendor Website URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    onBlur={(e) => {
                      const cleanedUrl = extractFQDN(e.target.value)
                      if (cleanedUrl !== e.target.value) {
                        setFormData({ ...formData, websiteUrl: cleanedUrl })
                      }
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()

                        // Normalize URL: add https:// if missing protocol
                        let url = e.currentTarget.value.trim()
                        if (url && !url.match(/^https?:\/\//i)) {
                          url = 'https://' + url
                          setFormData({ ...formData, websiteUrl: url })
                        }

                        // Wait a tick for state to update, then trigger Next button logic
                        setTimeout(async () => {
                          if (!url) {
                            alert('Please enter a website URL')
                            return
                          }

                          // If API key exists, auto-trigger discovery
                          if (hasApiKey) {
                            setDiscovering(true)
                            try {
                              await handleDiscoverVendor(url)
                              setFormStep(1)
                            } catch (error) {
                              console.error('[Step 0] Auto-discovery failed:', error)
                              setFormStep(1)
                            } finally {
                              setDiscovering(false)
                            }
                          } else {
                            // No API key, just advance to manual entry
                            setFormStep(1)
                          }
                        }, 50)
                      }
                    }}
                    placeholder="https://www.vendor.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll extract the vendor name and other details from their website
                  </p>
                </div>
              </div>
            )}

            {/* Step 1: Basic Information */}
            {formStep === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Vendor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vendorName}
                    onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                    placeholder="e.g., Acme Security Inc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Vendor Website URL *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      required
                      value={formData.websiteUrl}
                      onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                      onBlur={(e) => {
                        const cleanedUrl = extractFQDN(e.target.value)
                        if (cleanedUrl !== e.target.value) {
                          setFormData({ ...formData, websiteUrl: cleanedUrl })
                        }
                      }}
                      placeholder="https://www.vendor.com"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={handleDiscoverVendor}
                      disabled={discovering || !formData.vendorName || !formData.websiteUrl}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      title="Use AI to analyze vendor website and generate purpose and description"
                    >
                      {discovering ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Query Vendor
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Click "Query Vendor" to auto-populate purpose and description fields
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose (3 words max)
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.purpose || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      const wordCount = value.trim().split(/\s+/).filter(w => w.length > 0).length
                      if (wordCount <= 3 || value === '') {
                        setFormData({
                          ...formData,
                          purpose: value,
                          questionnaire: {
                            ...formData.questionnaire,
                            servicePurpose: value
                          }
                        })
                      }
                    }}
                    placeholder="e.g., Password management tool"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A short 3-word summary of the vendor's primary function
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.aiSummary || ''}
                    onChange={(e) => setFormData({ ...formData, aiSummary: e.target.value })}
                    placeholder="e.g., Cloud-based password management and credential storage service..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A concise description of the vendor's service offering and security posture (2-3 sentences)
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Value (Annual)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.contractValue || ''}
                        onChange={(e) => setFormData({ ...formData, contractValue: e.target.value ? parseFloat(e.target.value) : undefined })}
                        placeholder="e.g., 12000"
                        min="0"
                        step="0.01"
                        className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Annual contract value in USD (optional)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Licenses
                    </label>
                    <input
                      type="number"
                      value={formData.numberOfLicenses || ''}
                      onChange={(e) => setFormData({ ...formData, numberOfLicenses: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="e.g., 50"
                      min="1"
                      step="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of licenses/seats purchased (optional)
                    </p>
                  </div>
                </div>

                {/* Subprocessor Checkbox */}
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="isSubprocessor"
                    checked={formData.isSubprocessor || false}
                    onChange={(e) => setFormData({ ...formData, isSubprocessor: e.target.checked })}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="isSubprocessor" className="block text-sm font-medium text-gray-900 cursor-pointer">
                      Mark as Subprocessor
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      Check this if the vendor will process data on your behalf (automatically checked if DPA is provided)
                    </p>
                  </div>
                </div>

                {/* AI Suggested Classification */}
                {suggestedClassification && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-purple-900 mb-1">
                          AI-Suggested Data Classification
                        </h4>
                        <div className="mb-2">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            suggestedClassification.level === 'L0' ? 'bg-red-100 text-red-800' :
                            suggestedClassification.level === 'L1' ? 'bg-orange-100 text-orange-800' :
                            suggestedClassification.level === 'L2' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {suggestedClassification.level} - {
                              suggestedClassification.level === 'L0' ? 'Customer Data' :
                              suggestedClassification.level === 'L1' ? 'PII' :
                              suggestedClassification.level === 'L2' ? 'Non-public' :
                              'Public'
                            }
                          </span>
                        </div>
                        <p className="text-sm text-purple-800 mb-3">
                          <span className="font-medium">Reasoning:</span> {suggestedClassification.reasoning}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-purple-700">
                            This is a suggestion based on AI analysis. You'll answer questions in the next steps to confirm the classification.
                          </p>
                          <button
                            type="button"
                            onClick={() => setSuggestedClassification(null)}
                            className="text-xs text-purple-600 hover:text-purple-800 underline ml-auto"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Upload className="h-4 w-4 inline mr-2" />
                    Upload Questionnaire (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Upload a completed vendor questionnaire document (PDF, Word, Excel, or text file, max 10MB)
                  </p>

                  {!uploadedFile ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                      <input
                        type="file"
                        id="questionnaire-upload"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="questionnaire-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Click to upload or drag and drop
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          PDF, Word, Excel, or Text (max 10MB)
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileIcon className="h-5 w-5 text-primary-600" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{uploadedFile.name}</div>
                            <div className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeUploadedFile}
                          className="p-1 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-50"
                          title="Remove file"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    This file will be preserved for reference and can be viewed after submission.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Privacy Policy Analysis */}
            {formStep === 2 && (
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Privacy Policy Analysis</h4>
                  <p className="text-sm text-purple-800">
                    We'll automatically scan the vendor's privacy policy to determine data access patterns.
                  </p>
                </div>

                {analyzingPrivacyPolicy && (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin text-4xl mb-4">⏳</div>
                      <p className="text-lg font-medium text-gray-900">{privacyPolicyAnalysisStatus}</p>
                      <p className="text-sm text-gray-600 mt-2">
                        Scanning {formData.websiteUrl} for privacy policy, DPA, and analyzing data access patterns
                      </p>
                    </div>
                  </div>
                )}

                {!analyzingPrivacyPolicy && privacyPolicyAnalysis && (
                  <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h5 className="text-md font-semibold text-gray-900 mb-4">Analysis Results</h5>

                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Privacy Policy URL:</label>
                          <p className="mt-1">
                            <a
                              href={privacyPolicyAnalysis.privacyPolicyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              {privacyPolicyAnalysis.privacyPolicyUrl}
                            </a>
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">DPA (Data Processing Agreement) URL:</label>
                          <p className="mt-1">
                            {privacyPolicyAnalysis.dpaUrl ? (
                              <a
                                href={privacyPolicyAnalysis.dpaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                              >
                                {privacyPolicyAnalysis.dpaUrl}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-500 italic">Not found on vendor website</span>
                            )}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="border rounded-lg p-4">
                            <label className="text-sm font-medium text-gray-700">Customer Data Access:</label>
                            <div className="mt-2">
                              {privacyPolicyAnalysis.accessCustomerData ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                  YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  NO
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="border rounded-lg p-4">
                            <label className="text-sm font-medium text-gray-700">Employee PII Access:</label>
                            <div className="mt-2">
                              {privacyPolicyAnalysis.accessEmployeePII ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                                  YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  NO
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {privacyPolicyAnalysis.dataTypes && privacyPolicyAnalysis.dataTypes.length > 0 && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Data Types Collected:</label>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {privacyPolicyAnalysis.dataTypes.map((type, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="text-sm font-medium text-gray-700">AI Reasoning:</label>
                          <p className="mt-1 text-sm text-gray-600">
                            {privacyPolicyAnalysis.analysisReason}
                          </p>
                        </div>

                        {privacyPolicyAnalysis.accessCustomerData && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <p className="text-sm text-orange-900">
                              ⚠️ This vendor has been automatically marked as a subprocessor and will require a Data Processing Agreement (DPA).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <input
                        type="text"
                        placeholder="Adjust privacy policy URL if needed"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        defaultValue={privacyPolicyAnalysis.privacyPolicyUrl}
                      />
                      <button
                        type="button"
                        onClick={handleAnalyzePrivacyPolicy}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                      >
                        Re-scan
                      </button>
                    </div>
                  </div>
                )}

                {!analyzingPrivacyPolicy && !privacyPolicyAnalysis && (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                    <div className="text-center mb-6">
                      <p className="text-sm font-medium text-gray-900 mb-2">
                        Could not automatically locate privacy policy
                      </p>
                      <p className="text-xs text-gray-600 mb-4">
                        Please manually indicate data access patterns below, or adjust the URL and retry the scan
                      </p>
                    </div>

                    {/* Manual Override Checkboxes */}
                    <div className="space-y-4 mb-6">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.questionnaire?.accessCustomerData === true}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                questionnaire: {
                                  ...formData.questionnaire,
                                  accessCustomerData: e.target.checked
                                }
                              })
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              This vendor has access to customer data
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Check if vendor processes customer contact info, account data, usage data, payment info, or any end-user data
                            </p>
                          </div>
                        </label>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.questionnaire?.accessEmployeePII === true}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                questionnaire: {
                                  ...formData.questionnaire,
                                  accessEmployeePII: e.target.checked
                                }
                              })
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              This vendor has access to employee PII
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Check if vendor processes employee names, emails, HR data, credentials, or workspace/collaboration data
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Retry Analysis Section */}
                    <div className="border-t border-yellow-200 pt-4">
                      <div className="flex items-center gap-4">
                        <input
                          type="text"
                          placeholder="Adjust privacy policy URL if needed"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          defaultValue={formData.websiteUrl}
                        />
                        <button
                          type="button"
                          onClick={handleAnalyzePrivacyPolicy}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm whitespace-nowrap"
                        >
                          Retry Scan
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Additional Context */}
            {formStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Context (Optional)
                  </label>
                  <textarea
                    value={formData.questionnaire.additionalContext}
                    onChange={(e) => setFormData({
                      ...formData,
                      questionnaire: { ...formData.questionnaire, additionalContext: e.target.value }
                    })}
                    placeholder="Any additional information about how this vendor will be used or data they'll access..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Ready to Analyze</h4>
                  <p className="text-sm text-green-800">
                    Click "Analyze Vendor" to check the vendor's security posture and determine their data classification level.
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Attestation Confirmation (if needed) */}
            {formStep === 5 && (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                        Security Attestation Verification
                      </h4>
                      <p className="text-sm text-yellow-800 mb-3">
                        Based on your data classification, the following security attestations are required or recommended for this vendor.
                        <span className="font-semibold"> Attestations marked as REQUIRED must be confirmed for auto-approval.</span>
                      </p>
                      <p className="text-sm text-yellow-800">
                        Please check the vendor's website or documentation, select which attestations they have, and provide proof (upload a document OR enter a URL):
                      </p>
                    </div>
                  </div>
                </div>

                {/* Interactive Checkboxes with File Upload */}
                <div className="space-y-3">
                  {uncertainAttestations.map((attestation) => {
                    const isSelected = confirmedAttestations.includes(attestation.label)
                    const uploadedFile = attestationFiles.get(attestation.label)
                    const borderColor = attestation.required ? 'border-red-400' : 'border-gray-200'
                    const bgColor = attestation.required ? 'bg-red-50' : 'bg-white'
                    const hoverColor = attestation.required ? 'hover:bg-red-100' : 'hover:bg-gray-50'

                    return (
                      <div
                        key={attestation.id}
                        className={`p-4 rounded-lg border-2 ${
                          isSelected
                            ? attestation.required ? 'bg-red-100 border-red-500' : 'bg-blue-50 border-blue-400'
                            : `${bgColor} ${borderColor} ${hoverColor}`
                        }`}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAttestationSelection(attestation.label)}
                            className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500 mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">{attestation.label}</span>
                              {attestation.required ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-600 text-white">
                                  REQUIRED
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-500 text-white">
                                  OPTIONAL
                                </span>
                              )}
                            </div>

                            {isSelected && (
                              <div className="mt-3 space-y-3">
                                <div className="text-xs text-gray-600 font-medium">
                                  {attestation.required ? 'Provide proof (required):' : 'Provide proof (optional):'}
                                </div>

                                {/* File Upload Section */}
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-700 font-medium">Upload Document:</div>
                                  {!uploadedFile ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="file"
                                        id={`attestation-file-${attestation.id}`}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0]
                                          if (file) {
                                            // Validate file size (max 10MB)
                                            if (file.size > 10 * 1024 * 1024) {
                                              alert('File size must be less than 10MB')
                                              e.target.value = ''
                                              return
                                            }
                                            handleAttestationFileUpload(attestation.label, file)
                                          }
                                        }}
                                        className="hidden"
                                      />
                                      <label
                                        htmlFor={`attestation-file-${attestation.id}`}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                                      >
                                        <Upload className="h-3 w-3" />
                                        Choose File
                                      </label>
                                      <span className="text-xs text-gray-500">PDF, Word, or Image (max 10MB)</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-2 bg-white border border-gray-300 rounded-md">
                                      <FileIcon className="h-4 w-4 text-green-600" />
                                      <span className="text-xs text-gray-900 flex-1 truncate">{uploadedFile.name}</span>
                                      <span className="text-xs text-gray-500">
                                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleAttestationFileUpload(attestation.label, null)}
                                        className="p-1 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-50"
                                        title="Remove file"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* OR Divider */}
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 border-t border-gray-300"></div>
                                  <span className="text-xs text-gray-500 font-medium">OR</span>
                                  <div className="flex-1 border-t border-gray-300"></div>
                                </div>

                                {/* URL Input Section */}
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-700 font-medium">Enter URL:</div>
                                  {!attestationUrls.has(attestation.label) ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="url"
                                        placeholder="https://example.com/soc2-report.pdf"
                                        className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault()
                                            const input = e.target as HTMLInputElement
                                            if (input.value.trim()) {
                                              handleAttestationUrlInput(attestation.label, input.value)
                                              input.value = ''
                                            }
                                          }
                                        }}
                                        onBlur={(e) => {
                                          if (e.target.value.trim()) {
                                            handleAttestationUrlInput(attestation.label, e.target.value)
                                            e.target.value = ''
                                          }
                                        }}
                                      />
                                      <span className="text-xs text-gray-500 whitespace-nowrap">Press Enter</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-300 rounded-md">
                                      <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                      <a
                                        href={attestationUrls.get(attestation.label)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline flex-1 truncate"
                                      >
                                        {attestationUrls.get(attestation.label)}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleAttestationUrlInput(attestation.label, null)}
                                        className="p-1 text-blue-600 hover:text-red-600 rounded-md hover:bg-red-50 flex-shrink-0"
                                        title="Remove URL"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    )
                  })}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Check the vendor's website (<a href={formData.websiteUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">{formData.websiteUrl}</a>)
                    or contact them directly to verify their security certifications. You can upload proof documents OR provide URLs to where the evidence is located.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Ready to Submit</h4>
                  <p className="text-sm text-green-800">
                    Once you've confirmed the vendor's attestations and provided proof (documents or URLs), click "Submit for Analysis" to complete the assessment.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  if (formStep === 5) {
                    // Go back to step 4 from attestation step
                    setFormStep(5)
                    setUncertainAttestations([])
                    setConfirmedAttestations([])
                  } else {
                    setFormStep(Math.max(1, formStep - 1))
                  }
                }}
                disabled={formStep === 0 || analyzingVendor || isSubmitting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex gap-3">
                {formStep < 3 ? (
                  <button
                    type="button"
                    onClick={async () => {
                      // Step 0: Validate website URL and trigger auto-discovery
                      if (formStep === 0) {
                        if (!formData.websiteUrl) {
                          alert('Please enter a website URL')
                          return
                        }

                        // Normalize URL: add https:// if missing protocol
                        let normalizedUrl = formData.websiteUrl.trim()
                        if (normalizedUrl && !normalizedUrl.match(/^https?:\/\//i)) {
                          normalizedUrl = 'https://' + normalizedUrl
                        }

                        // If API key exists, auto-trigger discovery
                        if (hasApiKey) {
                          setDiscovering(true)
                          try {
                            await handleDiscoverVendor(normalizedUrl)
                            // handleDiscoverVendor already sets formData, just advance
                            setFormStep(1)
                          } catch (error) {
                            console.error('[Step 0] Auto-discovery failed:', error)
                            // Still advance to manual entry
                            setFormStep(1)
                          } finally {
                            setDiscovering(false)
                          }
                        } else {
                          // No API key, just advance to manual entry
                          setFormStep(1)
                        }
                        return
                      }

                      // Step 1: Validate basic information
                      if (formStep === 1 && (!formData.vendorName || !formData.websiteUrl || !formData.questionnaire.servicePurpose)) {
                        console.log('[Step 1 Validation] Missing fields:', {
                          vendorName: formData.vendorName,
                          websiteUrl: formData.websiteUrl,
                          servicePurpose: formData.questionnaire.servicePurpose
                        })
                        alert('Please fill in all required fields')
                        return
                      }

                      setFormStep(formStep + 1)
                    }}
                    disabled={discovering}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {discovering && formStep === 0 && <Loader2 className="h-4 w-4 animate-spin" />}
                    {discovering && formStep === 0 ? 'Discovering...' : 'Next'}
                  </button>
                ) : formStep === 3 ? (
                  <button
                    type="submit"
                    disabled={analyzingVendor}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {analyzingVendor && <Loader2 className="h-4 w-4 animate-spin" />}
                    {analyzingVendor ? 'Analyzing...' : 'Analyze Vendor'}
                  </button>
                ) : formStep === 3 ? (
                  <button
                    type="button"
                    onClick={() => submitFinalAssessment(confirmedAttestations)}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit for Analysis'}
                  </button>
                ) : formStep === 5 ? (
                  <button
                    type="button"
                    onClick={() => submitFinalAssessment(confirmedAttestations)}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                ) : null}
              </div>
            </div>
          </form>
              </div>
            )}
          </React.Fragment>
          ))
        )}
      </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div data-tour="tprm-settings">
              <TPRMSettings />
            </div>
          )}
        </div>
      </div>

      {/* Vendor Assessment Modal - for both new and edit */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-4xl w-full my-8">
            {/* Modal content will go here */}
            <div className="max-h-[90vh] overflow-y-auto">
              {/* Form header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingVendor ? `Re-assess Vendor: ${editingVendor.vendorName}` : 'New Vendor Assessment'}
                    </h3>
                    {editingVendor && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Creating new assessment for {editingVendor.vendorName}. Previous assessment will be preserved.
                      </p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Step {formStep} of {formStep === 5 ? 5 : 4}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowForm(false)
                      setFormStep(1)
                      setEditingVendor(null)
                    }}
                    className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600 transition-all duration-300"
                      style={{ width: `${(formStep / (formStep === 5 ? 5 : 4)) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Form body */}
              <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 0: Website URL Only */}
            {formStep === 0 && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-2">Quick Start</h3>
                  <p className="text-sm text-blue-700">
                    Enter the vendor's website URL and we'll automatically discover their information using AI
                    {hasApiKey === false && <span className="text-orange-600 font-medium"> (Note: You'll need to set up an Anthropic API key in Settings to use auto-discovery)</span>}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Vendor Website URL *
                  </label>
                  <input
                    type="url"
                    required
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    onBlur={(e) => {
                      const cleanedUrl = extractFQDN(e.target.value)
                      if (cleanedUrl !== e.target.value) {
                        setFormData({ ...formData, websiteUrl: cleanedUrl })
                      }
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()

                        // Normalize URL: add https:// if missing protocol
                        let url = e.currentTarget.value.trim()
                        if (url && !url.match(/^https?:\/\//i)) {
                          url = 'https://' + url
                          setFormData({ ...formData, websiteUrl: url })
                        }

                        // Wait a tick for state to update, then trigger Next button logic
                        setTimeout(async () => {
                          if (!url) {
                            alert('Please enter a website URL')
                            return
                          }

                          // If API key exists, auto-trigger discovery
                          if (hasApiKey) {
                            setDiscovering(true)
                            try {
                              await handleDiscoverVendor(url)
                              setFormStep(1)
                            } catch (error) {
                              console.error('[Step 0] Auto-discovery failed:', error)
                              setFormStep(1)
                            } finally {
                              setDiscovering(false)
                            }
                          } else {
                            // No API key, just advance to manual entry
                            setFormStep(1)
                          }
                        }, 50)
                      }
                    }}
                    placeholder="https://www.vendor.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll extract the vendor name and other details from their website
                  </p>
                </div>
              </div>
            )}

            {/* Step 1: Basic Information */}
            {formStep === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="h-4 w-4 inline mr-2" />
                    Vendor Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vendorName}
                    onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                    placeholder="e.g., Acme Security Inc."
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Globe className="h-4 w-4 inline mr-2" />
                    Vendor Website URL *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      required
                      value={formData.websiteUrl}
                      onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                      onBlur={(e) => {
                        const cleanedUrl = extractFQDN(e.target.value)
                        if (cleanedUrl !== e.target.value) {
                          setFormData({ ...formData, websiteUrl: cleanedUrl })
                        }
                      }}
                      placeholder="https://www.vendor.com"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button
                      type="button"
                      onClick={handleDiscoverVendor}
                      disabled={discovering || !formData.vendorName || !formData.websiteUrl}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                      title="Use AI to analyze vendor website and generate purpose and description"
                    >
                      {discovering ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4" />
                          Query Vendor
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Click "Query Vendor" to auto-populate purpose and description fields
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Purpose (3 words max)
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.purpose || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      const wordCount = value.trim().split(/\s+/).filter(w => w.length > 0).length
                      if (wordCount <= 3 || value === '') {
                        setFormData({
                          ...formData,
                          purpose: value,
                          questionnaire: {
                            ...formData.questionnaire,
                            servicePurpose: value
                          }
                        })
                      }
                    }}
                    placeholder="e.g., Password management tool"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A short 3-word summary of the vendor's primary function
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.aiSummary || ''}
                    onChange={(e) => setFormData({ ...formData, aiSummary: e.target.value })}
                    placeholder="e.g., Cloud-based password management and credential storage service..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    A concise description of the vendor's service offering and security posture (2-3 sentences)
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contract Value (Annual)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        value={formData.contractValue || ''}
                        onChange={(e) => setFormData({ ...formData, contractValue: e.target.value ? parseFloat(e.target.value) : undefined })}
                        placeholder="e.g., 12000"
                        min="0"
                        step="0.01"
                        className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Annual contract value in USD (optional)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Licenses
                    </label>
                    <input
                      type="number"
                      value={formData.numberOfLicenses || ''}
                      onChange={(e) => setFormData({ ...formData, numberOfLicenses: e.target.value ? parseInt(e.target.value) : undefined })}
                      placeholder="e.g., 50"
                      min="1"
                      step="1"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of licenses/seats purchased (optional)
                    </p>
                  </div>
                </div>

                {/* Subprocessor Checkbox */}
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="isSubprocessor"
                    checked={formData.isSubprocessor || false}
                    onChange={(e) => setFormData({ ...formData, isSubprocessor: e.target.checked })}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="isSubprocessor" className="block text-sm font-medium text-gray-900 cursor-pointer">
                      Mark as Subprocessor
                    </label>
                    <p className="text-xs text-gray-600 mt-1">
                      Check this if the vendor will process data on your behalf (automatically checked if DPA is provided)
                    </p>
                  </div>
                </div>

                {/* AI Suggested Classification */}
                {suggestedClassification && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Shield className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-purple-900 mb-1">
                          AI-Suggested Data Classification
                        </h4>
                        <div className="mb-2">
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            suggestedClassification.level === 'L0' ? 'bg-red-100 text-red-800' :
                            suggestedClassification.level === 'L1' ? 'bg-orange-100 text-orange-800' :
                            suggestedClassification.level === 'L2' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {suggestedClassification.level} - {
                              suggestedClassification.level === 'L0' ? 'Customer Data' :
                              suggestedClassification.level === 'L1' ? 'PII' :
                              suggestedClassification.level === 'L2' ? 'Non-public' :
                              'Public'
                            }
                          </span>
                        </div>
                        <p className="text-sm text-purple-800 mb-3">
                          <span className="font-medium">Reasoning:</span> {suggestedClassification.reasoning}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-purple-700">
                            This is a suggestion based on AI analysis. You'll answer questions in the next steps to confirm the classification.
                          </p>
                          <button
                            type="button"
                            onClick={() => setSuggestedClassification(null)}
                            className="text-xs text-purple-600 hover:text-purple-800 underline ml-auto"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Upload className="h-4 w-4 inline mr-2" />
                    Upload Questionnaire (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Upload a completed vendor questionnaire document (PDF, Word, Excel, or text file, max 10MB)
                  </p>

                  {!uploadedFile ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                      <input
                        type="file"
                        id="questionnaire-upload"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <label
                        htmlFor="questionnaire-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="h-8 w-8 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          Click to upload or drag and drop
                        </span>
                        <span className="text-xs text-gray-500 mt-1">
                          PDF, Word, Excel, or Text (max 10MB)
                        </span>
                      </label>
                    </div>
                  ) : (
                    <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileIcon className="h-5 w-5 text-primary-600" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{uploadedFile.name}</div>
                            <div className="text-xs text-gray-500">
                              {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removeUploadedFile}
                          className="p-1 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-50"
                          title="Remove file"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    This file will be preserved for reference and can be viewed after submission.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Privacy Policy Analysis */}
            {formStep === 2 && (
              <div className="space-y-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-900 mb-2">Privacy Policy Analysis</h4>
                  <p className="text-sm text-purple-800">
                    We'll automatically scan the vendor's privacy policy to determine data access patterns.
                  </p>
                </div>

                {analyzingPrivacyPolicy && (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <div className="animate-spin text-4xl mb-4">⏳</div>
                      <p className="text-lg font-medium text-gray-900">{privacyPolicyAnalysisStatus}</p>
                      <p className="text-sm text-gray-600 mt-2">
                        Scanning {formData.websiteUrl} for privacy policy, DPA, and analyzing data access patterns
                      </p>
                    </div>
                  </div>
                )}

                {!analyzingPrivacyPolicy && privacyPolicyAnalysis && (
                  <div className="space-y-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h5 className="text-md font-semibold text-gray-900 mb-4">Analysis Results</h5>

                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Privacy Policy URL:</label>
                          <p className="mt-1">
                            <a
                              href={privacyPolicyAnalysis.privacyPolicyUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 underline"
                            >
                              {privacyPolicyAnalysis.privacyPolicyUrl}
                            </a>
                          </p>
                        </div>

                        <div>
                          <label className="text-sm font-medium text-gray-700">DPA (Data Processing Agreement) URL:</label>
                          <p className="mt-1">
                            {privacyPolicyAnalysis.dpaUrl ? (
                              <a
                                href={privacyPolicyAnalysis.dpaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 underline"
                              >
                                {privacyPolicyAnalysis.dpaUrl}
                              </a>
                            ) : (
                              <span className="text-sm text-gray-500 italic">Not found on vendor website</span>
                            )}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="border rounded-lg p-4">
                            <label className="text-sm font-medium text-gray-700">Customer Data Access:</label>
                            <div className="mt-2">
                              {privacyPolicyAnalysis.accessCustomerData ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                                  YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  NO
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="border rounded-lg p-4">
                            <label className="text-sm font-medium text-gray-700">Employee PII Access:</label>
                            <div className="mt-2">
                              {privacyPolicyAnalysis.accessEmployeePII ? (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                                  YES
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                  NO
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {privacyPolicyAnalysis.dataTypes && privacyPolicyAnalysis.dataTypes.length > 0 && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">Data Types Collected:</label>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {privacyPolicyAnalysis.dataTypes.map((type, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="text-sm font-medium text-gray-700">AI Reasoning:</label>
                          <p className="mt-1 text-sm text-gray-600">
                            {privacyPolicyAnalysis.analysisReason}
                          </p>
                        </div>

                        {privacyPolicyAnalysis.accessCustomerData && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                            <p className="text-sm text-orange-900">
                              ⚠️ This vendor has been automatically marked as a subprocessor and will require a Data Processing Agreement (DPA).
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <input
                        type="text"
                        placeholder="Adjust privacy policy URL if needed"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                        defaultValue={privacyPolicyAnalysis.privacyPolicyUrl}
                      />
                      <button
                        type="button"
                        onClick={handleAnalyzePrivacyPolicy}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                      >
                        Re-scan
                      </button>
                    </div>
                  </div>
                )}

                {!analyzingPrivacyPolicy && !privacyPolicyAnalysis && (
                  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                    <div className="text-center mb-6">
                      <p className="text-sm font-medium text-gray-900 mb-2">
                        Could not automatically locate privacy policy
                      </p>
                      <p className="text-xs text-gray-600 mb-4">
                        Please manually indicate data access patterns below, or adjust the URL and retry the scan
                      </p>
                    </div>

                    {/* Manual Override Checkboxes */}
                    <div className="space-y-4 mb-6">
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.questionnaire?.accessCustomerData === true}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                questionnaire: {
                                  ...formData.questionnaire,
                                  accessCustomerData: e.target.checked
                                }
                              })
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              This vendor has access to customer data
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Check if vendor processes customer contact info, account data, usage data, payment info, or any end-user data
                            </p>
                          </div>
                        </label>
                      </div>

                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <label className="flex items-start cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.questionnaire?.accessEmployeePII === true}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                questionnaire: {
                                  ...formData.questionnaire,
                                  accessEmployeePII: e.target.checked
                                }
                              })
                            }}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="ml-3">
                            <span className="text-sm font-medium text-gray-900">
                              This vendor has access to employee PII
                            </span>
                            <p className="text-xs text-gray-600 mt-1">
                              Check if vendor processes employee names, emails, HR data, credentials, or workspace/collaboration data
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* Retry Analysis Section */}
                    <div className="border-t border-yellow-200 pt-4">
                      <div className="flex items-center gap-4">
                        <input
                          type="text"
                          placeholder="Adjust privacy policy URL if needed"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                          defaultValue={formData.websiteUrl}
                        />
                        <button
                          type="button"
                          onClick={handleAnalyzePrivacyPolicy}
                          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm whitespace-nowrap"
                        >
                          Retry Scan
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Additional Context */}
            {formStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Context (Optional)
                  </label>
                  <textarea
                    value={formData.questionnaire.additionalContext}
                    onChange={(e) => setFormData({
                      ...formData,
                      questionnaire: { ...formData.questionnaire, additionalContext: e.target.value }
                    })}
                    placeholder="Any additional information about how this vendor will be used or data they'll access..."
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Ready to Analyze</h4>
                  <p className="text-sm text-green-800">
                    Click "Analyze Vendor" to check the vendor's security posture and determine their data classification level.
                  </p>
                </div>
              </div>
            )}

            {/* Step 5: Attestation Confirmation (if needed) */}
            {formStep === 5 && (
              <div className="space-y-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-yellow-900 mb-2">
                        Security Attestation Verification
                      </h4>
                      <p className="text-sm text-yellow-800 mb-3">
                        Based on your data classification, the following security attestations are required or recommended for this vendor.
                        <span className="font-semibold"> Attestations marked as REQUIRED must be confirmed for auto-approval.</span>
                      </p>
                      <p className="text-sm text-yellow-800">
                        Please check the vendor's website or documentation, select which attestations they have, and provide proof (upload a document OR enter a URL):
                      </p>
                    </div>
                  </div>
                </div>

                {/* Interactive Checkboxes with File Upload */}
                <div className="space-y-3">
                  {uncertainAttestations.map((attestation) => {
                    const isSelected = confirmedAttestations.includes(attestation.label)
                    const uploadedFile = attestationFiles.get(attestation.label)
                    const borderColor = attestation.required ? 'border-red-400' : 'border-gray-200'
                    const bgColor = attestation.required ? 'bg-red-50' : 'bg-white'
                    const hoverColor = attestation.required ? 'hover:bg-red-100' : 'hover:bg-gray-50'

                    return (
                      <div
                        key={attestation.id}
                        className={`p-4 rounded-lg border-2 ${
                          isSelected
                            ? attestation.required ? 'bg-red-100 border-red-500' : 'bg-blue-50 border-blue-400'
                            : `${bgColor} ${borderColor} ${hoverColor}`
                        }`}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAttestationSelection(attestation.label)}
                            className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500 mt-0.5"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">{attestation.label}</span>
                              {attestation.required ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-red-600 text-white">
                                  REQUIRED
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-500 text-white">
                                  OPTIONAL
                                </span>
                              )}
                            </div>

                            {isSelected && (
                              <div className="mt-3 space-y-3">
                                <div className="text-xs text-gray-600 font-medium">
                                  {attestation.required ? 'Provide proof (required):' : 'Provide proof (optional):'}
                                </div>

                                {/* File Upload Section */}
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-700 font-medium">Upload Document:</div>
                                  {!uploadedFile ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="file"
                                        id={`attestation-file-${attestation.id}`}
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        onChange={(e) => {
                                          const file = e.target.files?.[0]
                                          if (file) {
                                            // Validate file size (max 10MB)
                                            if (file.size > 10 * 1024 * 1024) {
                                              alert('File size must be less than 10MB')
                                              e.target.value = ''
                                              return
                                            }
                                            handleAttestationFileUpload(attestation.label, file)
                                          }
                                        }}
                                        className="hidden"
                                      />
                                      <label
                                        htmlFor={`attestation-file-${attestation.id}`}
                                        className="flex items-center gap-2 px-3 py-1.5 text-xs bg-white border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
                                      >
                                        <Upload className="h-3 w-3" />
                                        Choose File
                                      </label>
                                      <span className="text-xs text-gray-500">PDF, Word, or Image (max 10MB)</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-2 bg-white border border-gray-300 rounded-md">
                                      <FileIcon className="h-4 w-4 text-green-600" />
                                      <span className="text-xs text-gray-900 flex-1 truncate">{uploadedFile.name}</span>
                                      <span className="text-xs text-gray-500">
                                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleAttestationFileUpload(attestation.label, null)}
                                        className="p-1 text-gray-500 hover:text-red-600 rounded-md hover:bg-red-50"
                                        title="Remove file"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>

                                {/* OR Divider */}
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 border-t border-gray-300"></div>
                                  <span className="text-xs text-gray-500 font-medium">OR</span>
                                  <div className="flex-1 border-t border-gray-300"></div>
                                </div>

                                {/* URL Input Section */}
                                <div className="space-y-2">
                                  <div className="text-xs text-gray-700 font-medium">Enter URL:</div>
                                  {!attestationUrls.has(attestation.label) ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="url"
                                        placeholder="https://example.com/soc2-report.pdf"
                                        className="flex-1 px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault()
                                            const input = e.target as HTMLInputElement
                                            if (input.value.trim()) {
                                              handleAttestationUrlInput(attestation.label, input.value)
                                              input.value = ''
                                            }
                                          }
                                        }}
                                        onBlur={(e) => {
                                          if (e.target.value.trim()) {
                                            handleAttestationUrlInput(attestation.label, e.target.value)
                                            e.target.value = ''
                                          }
                                        }}
                                      />
                                      <span className="text-xs text-gray-500 whitespace-nowrap">Press Enter</span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-300 rounded-md">
                                      <Globe className="h-4 w-4 text-blue-600 flex-shrink-0" />
                                      <a
                                        href={attestationUrls.get(attestation.label)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-800 underline flex-1 truncate"
                                      >
                                        {attestationUrls.get(attestation.label)}
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleAttestationUrlInput(attestation.label, null)}
                                        className="p-1 text-blue-600 hover:text-red-600 rounded-md hover:bg-red-50 flex-shrink-0"
                                        title="Remove URL"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    )
                  })}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Check the vendor's website (<a href={formData.websiteUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">{formData.websiteUrl}</a>)
                    or contact them directly to verify their security certifications. You can upload proof documents OR provide URLs to where the evidence is located.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-900 mb-2">Ready to Submit</h4>
                  <p className="text-sm text-green-800">
                    Once you've confirmed the vendor's attestations and provided proof (documents or URLs), click "Submit for Analysis" to complete the assessment.
                  </p>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  if (formStep === 5) {
                    // Go back to step 4 from attestation step
                    setFormStep(5)
                    setUncertainAttestations([])
                    setConfirmedAttestations([])
                  } else {
                    setFormStep(Math.max(1, formStep - 1))
                  }
                }}
                disabled={formStep === 0 || analyzingVendor || isSubmitting}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex gap-3">
                {formStep < 3 ? (
                  <button
                    type="button"
                    onClick={async () => {
                      // Step 0: Validate website URL and trigger auto-discovery
                      if (formStep === 0) {
                        if (!formData.websiteUrl) {
                          alert('Please enter a website URL')
                          return
                        }

                        // Normalize URL: add https:// if missing protocol
                        let normalizedUrl = formData.websiteUrl.trim()
                        if (normalizedUrl && !normalizedUrl.match(/^https?:\/\//i)) {
                          normalizedUrl = 'https://' + normalizedUrl
                        }

                        // If API key exists, auto-trigger discovery
                        if (hasApiKey) {
                          setDiscovering(true)
                          try {
                            await handleDiscoverVendor(normalizedUrl)
                            // handleDiscoverVendor already sets formData, just advance
                            setFormStep(1)
                          } catch (error) {
                            console.error('[Step 0] Auto-discovery failed:', error)
                            // Still advance to manual entry
                            setFormStep(1)
                          } finally {
                            setDiscovering(false)
                          }
                        } else {
                          // No API key, just advance to manual entry
                          setFormStep(1)
                        }
                        return
                      }

                      // Step 1: Validate basic information
                      if (formStep === 1 && (!formData.vendorName || !formData.websiteUrl || !formData.questionnaire.servicePurpose)) {
                        console.log('[Step 1 Validation] Missing fields:', {
                          vendorName: formData.vendorName,
                          websiteUrl: formData.websiteUrl,
                          servicePurpose: formData.questionnaire.servicePurpose
                        })
                        alert('Please fill in all required fields')
                        return
                      }

                      setFormStep(formStep + 1)
                    }}
                    disabled={discovering}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {discovering && formStep === 0 && <Loader2 className="h-4 w-4 animate-spin" />}
                    {discovering && formStep === 0 ? 'Discovering...' : 'Next'}
                  </button>
                ) : formStep === 3 ? (
                  <button
                    type="submit"
                    disabled={analyzingVendor}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {analyzingVendor && <Loader2 className="h-4 w-4 animate-spin" />}
                    {analyzingVendor ? 'Analyzing...' : 'Analyze Vendor'}
                  </button>
                ) : formStep === 3 ? (
                  <button
                    type="button"
                    onClick={() => submitFinalAssessment(confirmedAttestations)}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit for Analysis'}
                  </button>
                ) : formStep === 5 ? (
                  <button
                    type="button"
                    onClick={() => submitFinalAssessment(confirmedAttestations)}
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                ) : null}
              </div>
            </div>
          </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCsvUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-primary-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Upload className="h-6 w-6" />
                  <h3 className="text-xl font-bold">Upload Vendor CSV</h3>
                </div>
                <button
                  onClick={() => {
                    setShowCsvUpload(false)
                    setCsvFile(null)
                    setCsvUploadResults({success: 0, errors: []})
                    setCsvProcessingProgress({current: 0, total: 0})
                  }}
                  disabled={uploadingCsv}
                  className="text-white hover:bg-primary-700 p-1 rounded transition-colors disabled:opacity-50"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Bulk Upload Vendors
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Upload a CSV file containing vendor information. All vendors will be added with a "Pending Review" status for later assessment.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-gray-700">
                  <p className="font-medium mb-1">Required CSV Columns:</p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li><strong>Vendor Name</strong> (required)</li>
                    <li><strong>Website</strong> or <strong>Website URL</strong> (optional)</li>
                    <li><strong>Service Purpose</strong> or <strong>Description</strong> (optional)</li>
                    <li><strong>Company Owner</strong> or <strong>Owner</strong> (optional)</li>
                    <li><strong>Vendor Email</strong>, <strong>Vendor Email Address</strong>, or <strong>Email</strong> (optional)</li>
                    <li><strong>Data Classification</strong>, <strong>Classification</strong>, or <strong>Data Class</strong> (optional, L0/L1/L2/L3 - skips AI analysis if provided)</li>
                    <li><strong>Purpose</strong> or <strong>3-Word Purpose</strong> (optional, max 3 words - concise vendor purpose statement)</li>
                    <li><strong>Is Subprocessor</strong> or <strong>Subprocessor</strong> (optional, yes/no)</li>
                    <li><strong>HIPAA</strong> or <strong>Is HIPAA</strong> (optional, yes/no)</li>
                    <li><strong>BAA</strong> or <strong>Has BAA</strong> (optional, yes/no)</li>
                  </ul>
                </div>
              </div>

              {/* File Upload Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {!csvFile ? (
                    <>
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            // Validate file type
                            if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
                              alert('Please select a CSV file')
                              return
                            }
                            setCsvFile(file)
                            setCsvUploadResults({success: 0, errors: []})
                          }
                        }}
                        disabled={uploadingCsv}
                        className="hidden"
                        id="csv-file-upload"
                      />
                      <label
                        htmlFor="csv-file-upload"
                        className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Choose CSV File
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Select a CSV file with vendor information
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center justify-between bg-gray-50 rounded p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-gray-900 font-medium">{csvFile.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(csvFile.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          setCsvFile(null)
                          setCsvUploadResults({success: 0, errors: []})
                        }}
                        disabled={uploadingCsv}
                        className="text-red-600 hover:text-red-700 p-1 rounded transition-colors disabled:opacity-50"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Progress */}
              {uploadingCsv && csvProcessingProgress.total > 0 && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
                    <span className="text-sm font-medium text-gray-900">
                      Processing vendors... {csvProcessingProgress.current} of {csvProcessingProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(csvProcessingProgress.current / csvProcessingProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Upload Results */}
              {csvUploadResults.success > 0 || csvUploadResults.errors.length > 0 ? (
                <div className="mb-4 space-y-3">
                  {csvUploadResults.success > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-green-900">
                            Successfully added {csvUploadResults.success} vendor{csvUploadResults.success > 1 ? 's' : ''}
                          </p>
                          <p className="text-xs text-green-700 mt-1">
                            The vendors have been added with "Pending Review" status and will appear in your assessments list.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {csvUploadResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                        <p className="text-sm font-medium text-red-900">
                          {csvUploadResults.errors.length} error{csvUploadResults.errors.length > 1 ? 's' : ''} occurred:
                        </p>
                      </div>
                      <div className="ml-7 max-h-32 overflow-y-auto">
                        <ul className="text-xs text-red-700 space-y-1">
                          {csvUploadResults.errors.map((error, idx) => (
                            <li key={idx} className="break-words">{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowCsvUpload(false)
                    setCsvFile(null)
                    setCsvUploadResults({success: 0, errors: []})
                    setCsvProcessingProgress({current: 0, total: 0})
                  }}
                  disabled={uploadingCsv}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {csvUploadResults.success > 0 || csvUploadResults.errors.length > 0 ? 'Close' : 'Cancel'}
                </button>
                {!uploadingCsv && (csvUploadResults.success === 0 && csvUploadResults.errors.length === 0) && (
                  <button
                    onClick={handleCsvUpload}
                    disabled={!csvFile}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    Upload and Process
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exception Request Modal */}
      {showExceptionModal && exceptionVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-orange-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6" />
                  <h3 className="text-xl font-bold">Submit Exception Request</h3>
                </div>
                <button
                  onClick={() => setShowExceptionModal(false)}
                  className="text-white hover:bg-orange-700 p-1 rounded transition-colors"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Vendor: {exceptionVendor.vendorName}
                </h4>
                <p className="text-sm text-gray-600">
                  This vendor is missing required security artifacts. To proceed with using this vendor,
                  you must get approval from your manager and provide a screenshot of that approval
                  (e.g., email, Slack message, or other written confirmation).
                </p>
              </div>

              {/* Reason Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Exception Request <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={exceptionReason}
                  onChange={(e) => setExceptionReason(e.target.value)}
                  placeholder="Explain why this vendor is needed and why an exception should be granted..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  disabled={submittingException}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Include details about the business need, timeline, and any mitigating factors.
                </p>
              </div>

              {/* Screenshot Upload Field */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manager Approval Screenshot <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {!exceptionScreenshot ? (
                    <>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            // Validate file size (max 5MB)
                            if (file.size > 5 * 1024 * 1024) {
                              alert('File size must be less than 5MB')
                              return
                            }
                            setExceptionScreenshot(file)
                          }
                        }}
                        disabled={submittingException}
                        className="hidden"
                        id="exception-screenshot-upload"
                      />
                      <label
                        htmlFor="exception-screenshot-upload"
                        className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Choose File
                      </label>
                      <p className="text-xs text-gray-500 mt-2">
                        Upload a screenshot of your manager's approval (PNG, JPG, or PDF, max 5MB)
                      </p>
                    </>
                  ) : (
                    <div className="flex items-center justify-between bg-gray-50 rounded p-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-gray-900 font-medium">{exceptionScreenshot.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(exceptionScreenshot.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        onClick={() => setExceptionScreenshot(null)}
                        disabled={submittingException}
                        className="text-red-600 hover:text-red-700 p-1 rounded transition-colors"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 justify-between">
                <button
                  onClick={handleSubmitLater}
                  disabled={submittingException}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Submit Later
                </button>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowExceptionModal(false)}
                    disabled={submittingException}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitException}
                    disabled={submittingException || !exceptionReason.trim() || !exceptionScreenshot}
                    className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {submittingException && <Loader2 className="h-4 w-4 animate-spin" />}
                    {submittingException ? 'Submitting...' : 'Submit Exception Request'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Generation Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg">
              <div className="flex items-center gap-3">
                <Sparkles className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Generate AI Content</h2>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Select what you want to generate for {selectedVendorIds.size} vendor{selectedVendorIds.size > 1 ? 's' : ''}:
              </p>

              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generateDescription}
                    onChange={(e) => setGenerateDescription(e.target.checked)}
                    disabled={generatingContent}
                    className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Vendor Description</div>
                    <div className="text-xs text-gray-500">2-3 sentence summary of the vendor</div>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={generatePurpose}
                    onChange={(e) => setGeneratePurpose(e.target.checked)}
                    disabled={generatingContent}
                    className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Purpose (3 words max)</div>
                    <div className="text-xs text-gray-500">Brief purpose statement</div>
                  </div>
                </label>
              </div>

              {generatingContent && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">Generating AI content...</span>
                  </div>
                </div>
              )}

              {generateResults.success > 0 || generateResults.errors.length > 0 ? (
                <div className="space-y-3">
                  {generateResults.success > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                        <p className="text-sm font-medium text-green-900">
                          Successfully generated content for {generateResults.success} vendor{generateResults.success > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  )}

                  {generateResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                        <p className="text-sm font-medium text-red-900">
                          {generateResults.errors.length} error{generateResults.errors.length > 1 ? 's' : ''}:
                        </p>
                      </div>
                      <div className="ml-7 max-h-32 overflow-y-auto">
                        <ul className="text-xs text-red-700 space-y-1">
                          {generateResults.errors.map((error, idx) => (
                            <li key={idx} className="break-words">{error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="flex gap-3 justify-end px-6 pb-6">
              <button
                onClick={() => {
                  setShowGenerateModal(false)
                  setGenerateResults({success: 0, errors: []})
                  setGenerateDescription(true)
                  setGeneratePurpose(true)
                }}
                disabled={generatingContent}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {generateResults.success > 0 || generateResults.errors.length > 0 ? 'Close' : 'Cancel'}
              </button>
              {!generatingContent && (generateResults.success === 0 && generateResults.errors.length === 0) && (
                <button
                  onClick={handleBulkGenerate}
                  disabled={!generateDescription && !generatePurpose}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Generate
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vendor Detail View Modal - Read-only */}
      {viewingVendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-full sm:max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">{viewingVendor.vendorName}</h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setViewingVendor(null)
                      handleEdit(viewingVendor)
                    }}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Re-assess"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setViewingVendor(null)
                      handleArchive(viewingVendor.id, viewingVendor.vendorName)
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                    title="Archive"
                  >
                    <Archive className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => {
                      setViewingVendor(null)
                      handleMoveToRecycleBin(viewingVendor.id, viewingVendor.vendorName)
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                  <div className="border-l border-gray-300 h-6 mx-1" />
                  <button
                    onClick={() => setViewingVendor(null)}
                    className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
                    title="Close"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Website</label>
                    <div className="mt-1">
                      <a
                        href={viewingVendor.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:underline"
                      >
                        {viewingVendor.websiteUrl}
                      </a>
                    </div>
                  </div>
                  {viewingVendor.purpose && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Purpose</label>
                      <div className="mt-1 text-sm text-gray-900">{viewingVendor.purpose}</div>
                    </div>
                  )}
                  {viewingVendor.companyOwner && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Company Owner</label>
                      <div className="mt-1 text-sm text-gray-900">{viewingVendor.companyOwner}</div>
                    </div>
                  )}
                  {viewingVendor.vendorEmail && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Vendor Email</label>
                      <div className="mt-1 text-sm text-gray-900">{viewingVendor.vendorEmail}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Assessment Status */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Assessment Status</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Status</label>
                    <div className="mt-1">{getStatusBadge(viewingVendor.status)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Data Classification</label>
                    <div className="mt-1">{getDataClassificationBadge(viewingVendor.dataClassification)}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Subprocessor</label>
                    <div className="mt-1 text-sm text-gray-900">{viewingVendor.isSubprocessor ? 'Yes' : 'No'}</div>
                  </div>
                  {viewingVendor.isHIPAA !== undefined && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">HIPAA Relevant</label>
                      <div className="mt-1 text-sm text-gray-900">{viewingVendor.isHIPAA ? 'Yes' : 'No'}</div>
                    </div>
                  )}
                  {viewingVendor.hasBAA !== undefined && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">BAA in Place</label>
                      <div className="mt-1 text-sm text-gray-900">{viewingVendor.hasBAA ? 'Yes' : 'No'}</div>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-600">Submitted</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {new Date(viewingVendor.submittedAt).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Submitted By</label>
                    <div className="mt-1 text-sm text-gray-900">{viewingVendor.submittedBy}</div>
                  </div>
                </div>
              </div>

              {/* Service Purpose */}
              {viewingVendor.questionnaire?.servicePurpose && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Service Purpose</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900">{viewingVendor.questionnaire.servicePurpose}</p>
                  </div>
                </div>
              )}

              {/* Data Access */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Data Access</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Accesses Customer Data</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {viewingVendor.questionnaire?.accessCustomerData === true ? 'Yes' :
                       viewingVendor.questionnaire?.accessCustomerData === false ? 'No' : 'Not specified'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Accesses Employee PII</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {viewingVendor.questionnaire?.accessEmployeePII === true ? 'Yes' :
                       viewingVendor.questionnaire?.accessEmployeePII === false ? 'No' : 'Not specified'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Accesses Internal Data</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {viewingVendor.questionnaire?.accessInternalData === true ? 'Yes' :
                       viewingVendor.questionnaire?.accessInternalData === false ? 'No' : 'Not specified'}
                    </div>
                  </div>
                  {viewingVendor.questionnaire?.dataTypes && viewingVendor.questionnaire.dataTypes.length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-gray-600">Data Types</label>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {viewingVendor.questionnaire.dataTypes.map((type: string, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Security Posture */}
              {viewingVendor.securityPosture && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Security Posture</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Privacy Policy</span>
                      {viewingVendor.securityPosture.hasPrivacyPolicy ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">SOC 2</span>
                      {viewingVendor.securityPosture.hasSoc2 ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">ISO 27001</span>
                      {viewingVendor.securityPosture.hasIso27001 ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Penetration Testing</span>
                      {viewingVendor.securityPosture.hasPenetrationTesting ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">DPA</span>
                      {viewingVendor.securityPosture.hasDpa ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* AI Summary */}
              {viewingVendor.aiSummary && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">AI Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900">{viewingVendor.aiSummary}</p>
                  </div>
                </div>
              )}

              {/* Classification Reasoning */}
              {viewingVendor.classificationReasoning && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Classification Reasoning</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-900">{viewingVendor.classificationReasoning}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setViewingVendor(null)}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default VendorRiskAssessment
